import React, {
  useState,
  useEffect,
  useRef,
  lazy,
  Suspense,
  useCallback,
} from "react";
import axios from "axios";
import DynamicGeneratedDataViewer from "./DynamicGeneratedDataViewer";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { Theme } from "../../lib/theme";
import { FaArrowLeft, FaArrowRight, FaEdit, FaClock, FaUser, FaPlus, FaChevronDown, FaPlay, FaFileAlt, FaChartBar } from "react-icons/fa";
import { FiEdit2, FiPlus, FiRefreshCw, FiTrash2 } from "react-icons/fi";
import { useSelector, useDispatch } from "react-redux";
import { setDocumentShare } from "../../redux/slices/documentShareSlice";
import { DisplayMode, Outline } from "../../types/presentationView";
import Sidebar from "./Sidebar";
import { setIsOutlineGenerated } from "../../redux/slices/outlineSlice";
import "./viewpresentation.css";
import { DesktopHeading, MobileHeading } from "./Heading";
import { DesktopButtonSection, MobileButtonSection } from "./ActionButtons";

import { saveAs } from "file-saver";
// @ts-ignore
import HtmlDocx from "html-docx-js/dist/html-docx";
import { io } from "socket.io-client";

// Import environment variables

import { toast } from "react-toastify";
import ExportPresentationButton from './ExportPresentationButton';
import DocumentShareButton from '../shared/DocumentShareButton';
import { Plan } from "../../types/pricingTypes";
import { IpInfoResponse } from "../../types/authTypes";
// PricingModal removed - use external pricing page
import { openPricing } from '../../utils/openPricing'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";

// Lazily load the refine panels

const RewriteRefinePanel = lazy(() => import("./PresentationComponents/RewriteRefinePanel"));
const ToneAudiencePanel = lazy(() => import("./PresentationComponents/ToneAudiencePanel"));

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5001';

// Placeholder for a single section block
export const SectionBlock: React.FC<{
  type: "TITLE-TEXT" | "TITLE-IMAGE" | "TITLE-TEXT-IMAGE";
  onAdd?: () => void;
  onEdit?: () => void;
  onRefine?: () => void; // We'll pass handleRefineClick here
  onDelete?: () => void;
  children?: React.ReactNode;
}> = ({
  type,
  onAdd,
  onEdit,
  onRefine,
  onDelete,
  children,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      {/* Mobile Layout - Side by Side with Compact Buttons */}
      <div className="md:hidden flex flex-row items-start py-2 relative">
        {/* Main Content */}
        <div className="flex-1 pr-3">{children}</div>
        {/* Right Action Buttons - Vertical Stack for Mobile - Only show if any handlers are provided */}
        {(onAdd || onEdit || onRefine || onDelete) && (
          <div className="flex flex-col justify-start items-center gap-2 pl-3 border-l border-gray-200 min-w-[44px]">
            {onAdd && (
              <button
                onClick={onAdd}
                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100 transition-colors text-sm shadow-sm"
                aria-label="Add"
                type="button"
                title="Click to add below"
              >
                <FiPlus className="w-4 h-4" />
              </button>
            )}
            {onEdit && (
              <button
                onClick={onEdit}
                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100 transition-colors text-sm shadow-sm"
                aria-label="Edit"
                type="button"
                title="Click to edit"
              >
                <FiEdit2 className="w-4 h-4" />
              </button>
            )}
            {onRefine && (
              <button
                onClick={(e) => {
                  console.log("🔵 Refine button clicked in SectionBlock");
                  e.stopPropagation();
                  onRefine();
                }}
                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100 transition-colors text-sm shadow-sm"
                aria-label="Refine"
                type="button"
                title="Click to refine"
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} className="w-3 h-3" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={handleDeleteClick}
                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors text-sm shadow-sm"
                aria-label="Delete"
                type="button"
                title="Click to delete"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Desktop Layout - Side by Side */}
      <div className="hidden md:flex flex-row items-stretch py-2 relative group hover:shadow-md rounded-lg transition-shadow">
        {/* Left Add Button - Only show if onAdd handler is provided */}
        {onAdd && (
          <div className="flex flex-col justify-start items-center mr-4 pr-4 border-r border-gray-200">
            <button
              onClick={onAdd}
              className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100 transition-colors shadow-sm"
              aria-label="Add"
              type="button"
              title="Click to add below"
            >
              <FiPlus className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Main Content */}
        <div className="flex-1 flex flex-col">{children}</div>
        {/* Right Action Buttons - Only show if any handlers are provided */}
        {(onEdit || onRefine || onDelete) && (
          <div className="flex flex-col justify-start items-center ml-4 pl-4 gap-2 border-l border-gray-200">
            {onEdit && (
              <button
                onClick={onEdit}
                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100 transition-colors shadow-sm"
                aria-label="Edit"
                type="button"
                title="Click to edit"
              >
              <FiEdit2 className="w-4 h-4" />
              </button>
            )}
            {/* Render Refine button only when onRefine is provided (so it appears only for text-like blocks) */}
            {onRefine && (
              <button
                onClick={(e) => {
                  console.log("🔵 Refine button clicked in SectionBlock");
                  e.stopPropagation();
                  onRefine();
                }}
                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100 transition-colors shadow-sm"
                aria-label="Refine"
                type="button"
                title="Click to refine"
              >
                 <FontAwesomeIcon icon={faWandMagicSparkles} className="w-3 h-3" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={handleDeleteClick}
                className="w-8 h-8 flex items-center justify-center border border-gray-300 rounded-full hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors shadow-sm"
                aria-label="Delete"
                type="button"
                title="Click to delete"
              >
                <FiTrash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Component</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this component? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button 
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
              >
                No
              </button>
              <button 
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-white bg-red-600 rounded hover:bg-red-700"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ...existing code...
const sampleData = {
  outlines: [
    {
      title: "Executive Summary",
      outlineID: "outline1",
      type: "Points",
      position: 1,
    },
    {
      title: "Introduction and Background",
      outlineID: "outline2",
      type: "TextPlusImage",
      position: 2,
    },
    {
      title: "Market Analysis and Opportunity",
      outlineID: "outline3",
      type: "Points",
      position: 3,
    },
    {
      title: "Competitive Landscape",
      outlineID: "outline4",
      type: "Graphs",
      position: 4,
    },
    {
      title: "Product/Service Description",
      outlineID: "outline5",
      type: "Table",
      position: 5,
    },
    {
      title: "Business Model and Strategy",
      outlineID: "outline6",
      type: "TextPlusImage",
      position: 6,
    },
    {
      title: "Financial Projections and Funding",
      outlineID: "outline7",
      type: "Statistics",
      position: 7,
    },
    {
      title: "Team and Governance",
      outlineID: "outline8",
      type: "People",
      position: 8,
    },
    {
      title: "Implementation Timeline",
      outlineID: "outline9",
      type: "Timeline",
      position: 9,
    },
    {
      title: "Risk Assessment and Mitigation",
      outlineID: "outline10",
      type: "Points",
      position: 10,
    },
  ],
  slides: {
    outline1: [
      `# Executive Summary

Our comprehensive business plan outlines our vision to revolutionize the market with our innovative solution. We've identified a significant gap in the current offerings and developed a scalable approach to address these unmet needs.

## Key Highlights

- Targeting a $50B market with 15% annual growth
- Proprietary technology with 3 pending patents
- Experienced leadership team with proven track record
- Projected to reach profitability in 24 months
- Seeking $5M in Series A funding for 18 months of runway

Our competitive advantages include superior technology, strategic partnerships with industry leaders, and a customer-centric approach that has already resulted in strong initial traction with a 92% retention rate in our pilot program.

We are positioned for rapid market penetration, with a clear path to scale and multiple revenue streams established. Our go-to-market strategy leverages both direct and channel sales approaches, with a focus on enterprise clients in key verticals.`,
    ],

    outline2: [
      `# Introduction and Background

## Company Origin Story

Founded in 2023, our company emerged from the founders' firsthand experience with the limitations of existing solutions in the market. After spending a combined 25 years in the industry, our founding team identified persistent pain points that remained unaddressed by current offerings.

## Problem Statement

Organizations across industries are struggling with increasingly complex data management needs:

1. Data silos prevent holistic business insights
2. Manual processes create inefficiencies and errors
3. Legacy systems lack integration capabilities
4. Security and compliance requirements are difficult to meet
5. Scaling existing solutions is cost-prohibitive

These challenges result in significant operational inefficiencies, missed opportunities, and competitive disadvantages. According to industry research, companies lose an average of 20-30% in potential revenue due to these limitations.

## Industry Context

The industry is undergoing rapid transformation driven by:

- Increased regulatory requirements
- Shifting customer expectations toward digital-first experiences
- Growing data volumes requiring advanced analytics capabilities
- Emerging technologies enabling new service models
- Pressure to improve operational efficiency

These trends have created both challenges and opportunities for organizations across sectors, particularly in financial services, healthcare, and retail—our primary target markets.

## Market Need

Our extensive market research, including interviews with 200+ potential customers, revealed consistent and urgent demand for a solution that addresses these core needs:

- Unified data integration across disparate systems
- Automated workflows for common business processes
- Advanced analytics with actionable insights
- Scalable infrastructure that grows with the business
- Enterprise-grade security and compliance features

No existing solution adequately addresses all these needs simultaneously, creating the market gap our product is designed to fill.`,
    ],

    outline3: [
      `# Market Analysis and Opportunity

## Market Size and Growth

Our solution targets the global enterprise software market, currently valued at $457 billion and projected to reach $679 billion by 2028, representing a CAGR of 8.2%. Within this broader market, our specific segment—integrated business intelligence and workflow automation solutions—is growing at an accelerated rate of 14.5% annually.

### Target Market Segments

| Segment | Market Size | Growth Rate | Our Target Share |
|---------|------------|------------|-----------------|
| Financial Services | $86B | 9.7% | 2.5% |
| Healthcare | $72B | 12.3% | 1.8% |
| Retail | $53B | 10.1% | 3.0% |
| Manufacturing | $67B | 7.8% | 1.2% |
| Other | $179B | 6.5% | 0.5% |

## Customer Profiles

### Enterprise (250+ employees)
- Complex organizational structures
- Multiple legacy systems
- High data volume and complexity
- Stringent security requirements
- Average contract value: $250,000

### Mid-market (50-249 employees)
- Growing technical debt
- Scaling challenges
- Limited IT resources
- Need for quick implementation
- Average contract value: $85,000

### SMB (10-49 employees)
- Budget constraints
- Seeking rapid time-to-value
- Limited technical expertise
- Focus on core features
- Average contract value: $25,000

## Market Trends Driving Adoption

1. **Digital Transformation Initiatives**: 89% of enterprises have adopted or plan to adopt digital-first business strategies.

2. **Remote Work Acceleration**: Organizations are investing in tools that enable seamless collaboration and workflow management across distributed teams.

3. **AI and Automation Focus**: 76% of businesses are prioritizing AI-powered automation to improve operational efficiency.

4. **Data-Driven Decision Making**: Growing recognition of data as a strategic asset, with 83% of organizations planning to increase investments in data analytics.

5. **Regulatory Complexity**: Expanding compliance requirements creating demand for solutions with built-in governance capabilities.

## Addressable Market Calculation

Our initial target market consists of approximately:
- 5,000 enterprise accounts
- 25,000 mid-market accounts
- 100,000 SMB accounts

Based on projected penetration rates and average contract values, this represents a total addressable market (TAM) of $12.8 billion, with a serviceable addressable market (SAM) of $4.2 billion in our primary geographic regions.`,
    ],

    outline4: [
      `# Competitive Landscape

## Market Positioning Matrix

Our analysis of the competitive landscape reveals four primary competitor categories:

1. **Legacy Enterprise Vendors**: Established players with comprehensive but complex solutions
2. **Point Solution Providers**: Specialized tools addressing specific functions
3. **Modern Platform Competitors**: New entrants with platform approaches similar to ours
4. **Internal Solutions**: Custom-built systems developed in-house by organizations

## Key Competitors Analysis

### Enterprise Vendors
- **CompanyX**: Market leader with 23% market share, strong in financial services
  - Strengths: Brand recognition, extensive feature set, large customer base
  - Weaknesses: Complex implementation (avg. 9-12 months), high cost, dated UI/UX
  
- **EnterpriseY**: Second largest player with 18% market share
  - Strengths: Strong security features, extensive partner network
  - Weaknesses: Limited customization, poor API ecosystem, high maintenance costs

### Modern Competitors
- **StartupZ**: Fast-growing challenger, raised $120M Series C
  - Strengths: Modern UI, fast implementation, strong analytics
  - Weaknesses: Limited enterprise features, scaling issues, narrow integration options
  
- **TechCorp**: Recent entrant from adjacent market
  - Strengths: Strong brand in related category, aggressive pricing, large R&D budget
  - Weaknesses: Incomplete solution, limited domain expertise, evolving roadmap

### Point Solutions
- **DataCo**: Leader in data integration
  - Strengths: Deep technical capabilities, extensive connectors
  - Weaknesses: No workflow capabilities, requires technical expertise
  
- **WorkflowPro**: Specializes in process automation
  - Strengths: Intuitive workflow designer, rapid implementation
  - Weaknesses: Limited data handling, poor analytics, weak security controls

## Competitive Advantage Analysis

| Feature Category | Our Solution | Legacy Vendors | Modern Competitors | Point Solutions |
|-----------------|-------------|----------------|-------------------|----------------|
| Implementation Time | 4-8 weeks | 6-12 months | 4-12 weeks | 2-6 weeks |
| Total Cost of Ownership | Medium | Very High | Medium-High | Low (per solution) |
| User Experience | Excellent | Poor | Good | Varies |
| Integration Capabilities | Extensive | Limited | Moderate | Varies |
| Enterprise Security | Comprehensive | Comprehensive | Basic-Moderate | Limited |
| Analytics Capabilities | Advanced | Basic | Moderate | Limited |
| Customization | High | Limited | Moderate | Limited |
| Mobile Support | Native | Limited | Native | Varies |

## Barriers to Entry

The market has several significant barriers to entry that work in our favor as an established player:

1. **Technical Complexity**: Building an integrated platform requires significant technical expertise and development resources
2. **Domain Knowledge**: Deep understanding of industry-specific processes and requirements
3. **Data Security Requirements**: Extensive investments needed to meet enterprise security standards
4. **Integration Ecosystem**: Building and maintaining connectors to hundreds of systems
5. **Customer Acquisition Costs**: High cost of enterprise sales and marketing

## Competitive Strategy

Our competitive strategy leverages our key differentiators:

1. **Speed to Value**: Implementation in weeks vs. months for legacy vendors
2. **Unified Platform**: Eliminating the need for multiple point solutions
3. **Industry-Specific Solutions**: Pre-configured templates for key verticals
4. **Superior User Experience**: Focus on usability across technical and non-technical users
5. **Transparent Pricing**: All-inclusive pricing vs. complex, unpredictable models`,
    ],

    outline5: [
      `# Product/Service Description

## Core Platform Overview

Our product is a cloud-native, integrated business operations platform that unifies data integration, workflow automation, and business intelligence in a single solution. The platform is built on a microservices architecture using containerized deployment for maximum scalability and reliability.

## Key Components

### 1. Data Integration Layer
- 200+ pre-built connectors to common business systems
- Real-time and batch data processing capabilities
- Data transformation and cleansing tools
- Schema mapping and management
- Data quality monitoring and alerting

### 2. Workflow Automation Engine
- Visual process designer (no-code)
- Conditional logic and branching
- Role-based task routing
- Approval workflows
- Exception handling and escalation paths
- SLA monitoring and management

### 3. Analytics and Reporting
- Interactive dashboards and visualizations
- Self-service report builder
- Scheduled report distribution
- Embedded AI for predictive insights
- Anomaly detection and alerting
- Drill-down capabilities for root cause analysis

### 4. Administration and Governance
- Role-based access control
- Audit logging and compliance reporting
- Data lineage tracking
- Version control and change management
- Environment management (dev/test/prod)
- Backup and disaster recovery

## Technical Architecture

Our platform employs a modern, cloud-native architecture:

- **Frontend**: React-based SPA with responsive design
- **Backend**: Containerized microservices using Kubernetes
- **Database**: Polyglot persistence (PostgreSQL for transactional data, MongoDB for document storage, Redis for caching)
- **Integration**: REST APIs with OpenAPI specification, GraphQL for complex data queries
- **Security**: OAuth 2.0/OIDC authentication, AES-256 encryption at rest, TLS 1.3 in transit

## Deployment Options

The platform is available in three deployment models:

1. **SaaS**: Fully managed multi-tenant environment hosted on AWS/Azure/GCP
2. **Private Cloud**: Dedicated instance in customer's cloud environment
3. **Hybrid**: Core in SaaS with sensitive data processing on-premises

## Key Differentiators

### 1. Unified Experience
Unlike competitors that cobble together disparate systems through acquisition, our platform was built from the ground up as an integrated solution, providing a consistent user experience across all modules.

### 2. No-Code/Low-Code Flexibility
Business users can configure workflows and reports through intuitive interfaces, while developers can extend functionality through APIs and custom code when needed.

### 3. Embedded Intelligence
AI capabilities are woven throughout the platform, from intelligent data mapping suggestions to anomaly detection in business processes and predictive analytics.

### 4. Industry Accelerators
Pre-built templates, workflows, and dashboards for specific industries reduce implementation time by up to 60% compared to general-purpose solutions.

### 5. Enterprise-Grade Security with Consumer-Grade Usability
Meeting the most stringent security requirements without sacrificing usability through contextual permissions and intuitive interfaces.

## Product Roadmap Highlights

**Q4 2023**
- Advanced natural language querying
- Enhanced mobile experience
- Additional industry accelerators

**Q1 2024**
- Expanded AI capabilities
- Citizen developer sandbox
- Advanced document processing

**Q2 2024**
- Embedded collaboration tools
- Process mining capabilities
- Expanded API marketplace

**Q3-Q4 2024**
- Low-latency edge processing
- Enhanced compliance automation
- Blockchain integration for audit trails`,
    ],

    outline6: [
      `# Business Model and Strategy

## Revenue Model

Our business employs a multi-faceted revenue model designed to maximize customer lifetime value while providing flexibility to meet diverse customer needs:

### 1. Subscription Licensing (85% of Revenue)

| Plan | Target Segment | Price Range | Key Limitations |
|------|---------------|------------|----------------|
| Standard | SMB | $2,000-5,000/mo | Limited workflows, users, and data volume |
| Professional | Mid-market | $5,000-15,000/mo | Expanded capabilities with moderate limits |
| Enterprise | Large orgs | $15,000-50,000/mo | Custom limits, premium features, dedicated support |

All plans include:
- Core platform functionality
- Regular updates and maintenance
- Standard support
- Basic SLAs

Higher tiers provide:
- Priority support with faster response times
- Higher usage limits (users, workflows, API calls)
- Advanced features (SSO, custom branding, etc.)
- Dedicated customer success resources

### 2. Professional Services (10% of Revenue)
- Implementation services: $20,000-150,000
- Custom development: $200-250/hour
- Training packages: $5,000-25,000
- Strategic consulting: $2,500/day

### 3. Marketplace & Platform (5% of Revenue)
- Third-party integration fees: 20-30% revenue share
- Template marketplace: $500-5,000 per template
- Custom connector development: $5,000-20,000 per connector

## Go-To-Market Strategy

### Direct Sales Channel
- Inside sales team for SMB segment
- Field sales for mid-market and enterprise
- Account-based marketing for target enterprises
- Sales cycle: 30 days (SMB), 60 days (mid-market), 120+ days (enterprise)

### Partner Ecosystem
- Implementation partners: Systems integrators and consultancies
- Technology partners: Complementary software providers
- Industry partners: Vertical-specific solution providers
- Referral partners: 10% commission structure

### Marketing Strategy
- Content marketing focused on thought leadership
- Industry-specific webinars and events
- Digital demand generation campaigns
- Customer advocacy program
- Product-led growth motion for SMB segment

## Customer Lifecycle

1. **Acquisition**
   - Free trial or POC for qualified prospects
   - Solution workshops for complex requirements
   - ROI calculator and business case development

2. **Onboarding**
   - Structured implementation methodology
   - Role-based training programs
   - Quick-win identification and execution

3. **Adoption & Expansion**
   - Quarterly business reviews
   - Usage analytics and optimization recommendations
   - Cross-sell/upsell based on usage patterns
   - User community engagement

4. **Retention**
   - Proactive health monitoring
   - Executive sponsorship program
   - Product roadmap input opportunities
   - Customer advisory board for key accounts

## Unit Economics

### Customer Acquisition Cost (CAC)
- SMB: $15,000
- Mid-market: $35,000
- Enterprise: $85,000

### Lifetime Value (LTV)
- SMB: $96,000 (4-year average lifespan)
- Mid-market: $360,000 (5-year average lifespan)
- Enterprise: $1,200,000 (6-year average lifespan)

### LTV:CAC Ratio
- SMB: 6.4:1
- Mid-market: 10.3:1
- Enterprise: 14.1:1

### Payback Period
- SMB: 8 months
- Mid-market: 10 months
- Enterprise: 14 months`,
    ],

    outline7: [
      `# Financial Projections and Funding

## Historical Performance

| Metric | 2021 | 2022 | 2023 |
|--------|------|------|------|
| Revenue | $1.2M | $4.5M | $12.8M |
| YoY Growth | - | 275% | 184% |
| Gross Margin | 68% | 72% | 76% |
| CAC | $48K | $42K | $37K |
| LTV:CAC | 3.2:1 | 4.5:1 | 6.8:1 |
| ARR | $2.8M | $8.9M | $18.3M |
| Cash Burn | $450K/mo | $380K/mo | $220K/mo |

## 5-Year Financial Projections

### Revenue Forecast ($M)

| Revenue Stream | Year 1 | Year 2 | Year 3 | Year 4 | Year 5 |
|----------------|--------|--------|--------|--------|--------|
| Subscriptions | $24.6 | $52.8 | $96.4 | $156.2 | $234.3 |
| Services | $3.8 | $6.2 | $10.1 | $15.6 | $22.4 |
| Marketplace | $1.2 | $3.5 | $8.2 | $14.8 | $23.7 |
| **Total Revenue** | **$29.6** | **$62.5** | **$114.7** | **$186.6** | **$280.4** |
| YoY Growth | 131% | 111% | 84% | 63% | 50% |

### Cost Structure and Profitability ($M)

| Category | Year 1 | Year 2 | Year 3 | Year 4 | Year 5 |
|----------|--------|--------|--------|--------|--------|
| COGS | $7.1 | $14.4 | $25.2 | $39.2 | $56.1 |
| Gross Profit | $22.5 | $48.1 | $89.5 | $147.4 | $224.3 |
| Gross Margin | 76% | 77% | 78% | 79% | 80% |
| R&D | $8.9 | $15.6 | $25.2 | $35.5 | $47.7 |
| S&M | $18.7 | $28.1 | $43.6 | $63.4 | $84.1 |
| G&A | $5.3 | $8.8 | $13.8 | $19.6 | $25.2 |
| EBITDA | ($10.4) | ($4.4) | $6.9 | $28.9 | $67.3 |
| EBITDA Margin | -35% | -7% | 6% | 15% | 24% |

## Key Metrics

| KPI | Year 1 | Year 2 | Year 3 | Year 4 | Year 5 |
|-----|--------|--------|--------|--------|--------|
| Customer Count | 285 | 520 | 870 | 1,350 | 2,050 |
| Net Revenue Retention | 112% | 118% | 124% | 128% | 130% |
| CAC Payback (months) | 12 | 10 | 9 | 8 | 7 |
| Rule of 40 Score | 96% | 104% | 90% | 78% | 74% |
| ARR | $35M | $74M | $135M | $215M | $320M |

## Funding Requirements and Use of Funds

We are seeking $25 million in Series B funding to accelerate our growth trajectory. The capital will be allocated as follows:

### Use of Funds
- **Product Development (40%)**: $10M
  - Expand engineering team from 45 to 85 members
  - Accelerate roadmap delivery by 18 months
  - Develop additional industry-specific solutions
  - Enhance AI/ML capabilities

- **Go-to-Market Expansion (35%)**: $8.75M
  - Scale sales team from 25 to 60 members
  - Enter two new geographic markets
  - Expand channel partner program
  - Increase marketing spend for demand generation

- **Customer Success (15%)**: $3.75M
  - Build customer education platform
  - Expand implementation team
  - Develop self-service onboarding tools
  - Create customer community platform

- **Operations & Infrastructure (10%)**: $2.5M
  - Enhance security and compliance capabilities
  - Improve operational efficiency and automation
  - Scale internal systems and processes

### Expected Outcomes
- Accelerate revenue growth to 100%+ for next two years
- Reduce CAC payback period to under 8 months
- Improve gross margins to 80%+
- Achieve profitability by end of Year 2
- Position company for potential IPO or strategic exit by Year 4

## Exit Strategy

We anticipate three potential exit scenarios:

1. **IPO**: Target timeline of 4-5 years with $250M+ ARR and 30%+ growth rate

2. **Strategic Acquisition**: Several potential acquirers have expressed interest, including:
   - Enterprise software vendors seeking to modernize their offerings
   - Private equity firms focused on SaaS consolidation
   - Adjacent technology providers expanding their portfolios

3. **Private Equity Recapitalization**: Secondary option if market conditions aren't favorable for IPO`,
    ],

    outline8: [
      `# Team and Governance

## Leadership Team

### Jane Smith, CEO & Co-Founder
20+ years of enterprise software experience with leadership roles at ServiceNow, Oracle, and IBM. Led ServiceNow's expansion from $300M to $1.2B in annual revenue as SVP of Product. MBA from Stanford, BS Computer Science from MIT.

### Michael Chen, CTO & Co-Founder
Former CTO of cloud data integration startup acquired by Microsoft. Previously Principal Engineer at Amazon Web Services, where he led development of key services. PhD in Computer Science from Carnegie Mellon, specializing in distributed systems.

### Sarah Johnson, Chief Revenue Officer
Scaling revenue expert who built and led sales organizations at Workday and Salesforce. Took Workday's financial services vertical from $45M to $280M in annual revenue in 4 years. MBA from Wharton, BA Economics from UCLA.

### David Rodriguez, CFO
Former VP of Finance at Datadog, where he helped scale the company through IPO. Previously investment banker specializing in enterprise software at Goldman Sachs. CPA, MBA from Columbia, BS Accounting from NYU.

### Priya Patel, Chief Product Officer
Product leader with experience at Atlassian and Zendesk. Led Atlassian's workflow automation initiatives, growing that product line to $100M ARR. MS Product Design from Stanford, BS Computer Engineering from UC Berkeley.

### Robert Washington, Chief Customer Officer
Customer success executive with experience building post-sales organizations at Zoom and DocuSign. Established customer success methodologies that achieved 130%+ net revenue retention. MBA from University of Chicago, BA Psychology from Howard University.

## Board of Directors

### Current Board Members

- Jane Smith, CEO & Co-Founder
- Michael Chen, CTO & Co-Founder
- Alexandra Wilson, Partner at Accel (Series A lead)
- Thomas Grant, Managing Director at Sequoia Capital (Seed lead)
- Rajiv Mehta, Independent Director (former CIO of Fortune 100 financial services company)

### Board Observers

- James Peterson, Partner at Insight Partners
- Diana Rodriguez, VP at Andreessen Horowitz

## Advisory Board

We've assembled a world-class advisory board with expertise in our key domains:

- Dr. Elena Ivanova, AI/ML Expert (Stanford Professor, former Google Research)
- Richard Wong, Former CIO of Goldman Sachs
- Dr. Marcus Lee, Data Privacy & Security Expert (CISO of major healthcare provider)
- Jennifer Baker, Customer Experience Pioneer (founder of acquired CX platform)
- Thomas Richards, Enterprise Sales Strategy (former EVP Sales at Oracle)

## Organizational Structure

### Current Team Composition (125 employees)

| Department | Headcount | Key Focus Areas |
|------------|-----------|----------------|
| Engineering | 45 | Platform development, integrations, infrastructure |
| Product | 18 | Product management, design, documentation |
| Sales & Marketing | 32 | Direct sales, marketing, partnerships |
| Customer Success | 20 | Implementation, support, customer training |
| G&A | 10 | Finance, legal, HR, operations |

### 24-Month Hiring Plan

We plan to grow to 250+ employees with focus on:
- Engineering: Add 40 engineers (AI/ML, integrations, security)
- Sales: Expand field sales team by 35 across key markets
- Customer Success: Double team size to support growth
- International: Establish presence in Europe and APAC

## Governance Framework

### Corporate Structure
- Delaware C-Corporation
- Subsidiaries in UK and Singapore for international operations
- GDPR and local regulatory compliance architecture

### Policies and Procedures
- SOC 2 Type 2 compliant
- ISO 27001 certified
- Quarterly security audits
- Ethics and compliance training program

### Equity Structure
- Employee stock option plan (15% pool)
- 4-year vesting with 1-year cliff for all employees
- Performance-based equity incentives for executives
- Investor protections include standard liquidation preferences and anti-dilution provisions

## Culture and Values

Our company culture is built around five core values:

1. **Customer Obsession**: We make every decision with customer impact in mind
2. **Radical Transparency**: We share information openly and honestly
3. **Continuous Innovation**: We never stop improving our products and processes
4. **Data-Driven Decisions**: We use metrics to guide our choices
5. **Inclusive Collaboration**: We believe diverse teams build better solutions`,
    ],

    outline9: [
      `# Implementation Timeline

## Phase 1: Foundation Building (Months 1-6)

### Month 1: Project Initiation
- Assemble core implementation team
- Conduct stakeholder interviews
- Define detailed project scope and requirements
- Establish project governance structure
- Set up project management infrastructure
- Conduct technical environment assessment
- Develop detailed project plan

### Month 2: Technical Foundation
- Configure development environments
- Set up CI/CD pipelines
- Establish data architecture
- Deploy core infrastructure
- Configure security controls
- Implement monitoring and logging
- Conduct initial data assessment

### Month 3: Data Integration
- Connect to priority source systems
- Establish data transformation rules
- Implement data quality checks
- Develop initial data models
- Configure master data management
- Test data synchronization
- Document data flows

### Month 4: Core Functionality
- Configure user management
- Set up role-based access controls
- Implement core business logic
- Develop custom components
- Build initial dashboards
- Configure notification system
- Develop API integrations

### Month 5: Workflow Configuration
- Design workflow templates
- Configure approval hierarchies
- Set up task management
- Implement business rules
- Configure SLA monitoring
- Develop exception handling
- Test end-to-end processes

### Month 6: Testing & Training
- Conduct unit testing
- Perform integration testing
- Execute user acceptance testing
- Develop training materials
- Conduct administrator training
- Train end users
- Prepare for go-live

## Phase 2: Initial Deployment (Months 7-12)

### Month 7: Production Deployment
- Migrate to production environment
- Conduct final data validation
- Perform security testing
- Execute go-live checklist
- Activate monitoring systems
- Implement hypercare support
- Document deployment procedures

### Month 8: Stabilization
- Monitor system performance
- Address issues and bugs
- Optimize system configuration
- Conduct user feedback sessions
- Fine-tune workflows
- Adjust automation rules
- Enhance documentation

### Month 9: Process Optimization
- Analyze usage patterns
- Identify bottlenecks
- Refine business processes
- Enhance reporting
- Implement additional automations
- Optimize user experience
- Document best practices

### Month 10: Advanced Features
- Deploy additional modules
- Configure advanced analytics
- Implement AI capabilities
- Enhance integration points
- Develop custom dashboards
- Configure mobile experience
- Extend workflow capabilities

### Month 11: Expansion Planning
- Assess additional use cases
- Plan for geographic expansion
- Evaluate new integrations
- Develop scaling strategy
- Analyze performance metrics
- Document expansion requirements
- Prepare resource plans

### Month 12: Review & Scale
- Conduct annual system review
- Measure KPI achievement
- Plan next phase deployments
- Refresh training program
- Update documentation
- Prepare scaling infrastructure
- Present success metrics

## Phase 3: Enterprise Expansion (Months 13-24)

### Months 13-15: Department Expansion
- Onboard additional departments
- Implement department-specific workflows
- Develop specialized reports
- Configure department dashboards
- Train new user groups
- Extend data integrations
- Measure department adoption

### Months 16-18: Advanced Analytics
- Deploy predictive models
- Implement AI-driven insights
- Develop executive dashboards
- Configure anomaly detection
- Implement decision support tools
- Establish data governance
- Document analytics use cases

### Months 19-21: Ecosystem Integration
- Connect partner systems
- Implement B2B workflows
- Develop customer-facing portals
- Extend API capabilities
- Configure external access
- Implement federated security
- Test ecosystem performance

### Months 22-24: Global Deployment
- Deploy to international locations
- Configure localization
- Implement regional compliance
- Establish global support model
- Configure multi-region infrastructure
- Train international teams
- Document global operations

## Key Milestones & Success Metrics

| Milestone | Target Date | Success Metrics |
|-----------|------------|----------------|
| Project Kickoff | Month 1, Week 1 | Stakeholder alignment, approved project charter |
| Core Infrastructure | Month 2, Week 4 | System stability, performance benchmarks met |
| Data Integration | Month 3, Week 4 | 99.9% data accuracy, all sources connected |
| Initial Go-Live | Month 7, Week 1 | System uptime, user adoption rate >80% |
| First Optimization | Month 9, Week 2 | 30% process efficiency improvement |
| Advanced Features | Month 10, Week 4 | Feature utilization rates, user satisfaction |
| Full Enterprise Deployment | Month 18, Week 4 | 100% department coverage, 90% process automation |
| Global Operations | Month 24, Week 4 | Multi-region support, 24/7 operations enabled |

## Risk Management Timeline

| Risk Category | Monitoring Period | Mitigation Strategies |
|--------------|------------------|----------------------|
| Data Quality | Continuous | Regular validation, automated testing, data stewardship |
| System Performance | Daily | Load testing, scaling plan, performance optimization |
| User Adoption | Weeks 1-12 post go-live | Training, support, champions program, incentives |
| Integration Stability | Weekly checks | Monitoring, failover mechanisms, API versioning |
| Security & Compliance | Quarterly reviews | Penetration testing, compliance audits, security training |`,
    ],

    outline10: [
      `# Risk Assessment and Mitigation

## Strategic Risks

### Market Adoption Risk
**Risk Level: Medium**
- **Risk**: Target customers may be slower to adopt our solution than projected due to budget constraints, competing priorities, or resistance to change.
- **Impact**: Extended sales cycles, lower-than-projected revenue, and increased customer acquisition costs.
- **Mitigation Strategies**:
  1. Develop proof-of-concept programs with minimal commitment
  2. Create ROI calculator with case studies demonstrating concrete value
  3. Implement phased adoption approach allowing customers to start small
  4. Establish customer reference program to leverage peer validation

### Competitive Risk
**Risk Level: Medium-High**
- **Risk**: Established vendors or well-funded startups may rapidly develop comparable offerings or significantly drop prices.
- **Impact**: Market share erosion, pricing pressure, and reduced growth rate.
- **Mitigation Strategies**:
  1. Accelerate product roadmap to maintain feature differentiation
  2. Deepen vertical expertise to create industry-specific value
  3. Build ecosystem of partners and integrations to increase switching costs
  4. Monitor competitive landscape through dedicated competitive intelligence function

### Regulatory Risk
**Risk Level: Medium**
- **Risk**: Changes in data privacy, security, or industry-specific regulations may require significant product modifications.
- **Impact**: Increased development costs, delayed features, potential market exclusion.
- **Mitigation Strategies**:
  1. Maintain regulatory compliance team monitoring upcoming legislation
  2. Design product architecture with regulatory flexibility in mind
  3. Participate in industry associations influencing regulatory development
  4. Implement modular compliance frameworks adaptable to regional requirements

## Operational Risks

### Technical Debt Risk
**Risk Level: Medium**
- **Risk**: Rapid development pace may result in accumulation of technical debt impacting stability and scalability.
- **Impact**: Increased maintenance costs, slower feature development, stability issues.
- **Mitigation Strategies**:
  1. Allocate 20% of engineering capacity to debt reduction quarterly
  2. Implement architectural review board for major changes
  3. Maintain comprehensive test coverage (target >85%)
  4. Conduct regular system health assessments

### Scalability Risk
**Risk Level: Medium-Low**
- **Risk**: System may not scale effectively to handle projected customer growth and data volumes.
- **Impact**: Performance degradation, service disruptions, customer dissatisfaction.
- **Mitigation Strategies**:
  1. Implement regular load testing simulating 10x current volume
  2. Architect for horizontal scaling across all system components
  3. Establish performance baseline monitoring with automated alerts
  4. Develop capacity planning model updated quarterly

### Security Risk
**Risk Level: High**
- **Risk**: Security breach or data loss incident could severely impact customer trust and company reputation.
- **Impact**: Customer churn, legal liability, brand damage, potential regulatory penalties.
- **Mitigation Strategies**:
  1. Implement comprehensive security program (SOC 2, ISO 27001)
  2. Conduct quarterly third-party penetration testing
  3. Encrypt all sensitive data both in transit and at rest
  4. Establish incident response plan with regular drills
  5. Maintain cyber insurance with adequate coverage

### Talent Retention Risk
**Risk Level: Medium-High**
- **Risk**: Difficulty retaining key personnel in competitive talent market.
- **Impact**: Knowledge loss, project delays, increased recruitment costs.
- **Mitigation Strategies**:
  1. Implement competitive compensation with retention incentives
  2. Create career development paths for all roles
  3. Establish knowledge management system to preserve institutional knowledge
  4. Develop succession plans for key positions

## Financial Risks

### Cash Flow Risk
**Risk Level: Medium**
- **Risk**: Cash burn rate may exceed projections before achieving profitability.
- **Impact**: Potential need for emergency fundraising at unfavorable terms.
- **Mitigation Strategies**:
  1. Maintain 18+ months of runway based on conservative projections
  2. Implement tiered spending plan tied to revenue milestones
  3. Establish line of credit for short-term cash management
  4. Create early warning system with financial metrics monitored weekly

### Customer Concentration Risk
**Risk Level: Low-Medium**
- **Risk**: Overreliance on small number of large customers may create vulnerability to churn.
- **Impact**: Revenue volatility, reduced bargaining power, potential cash flow issues.
- **Mitigation Strategies**:
  1. Limit any single customer to <8% of ARR
  2. Diversify across industries and customer segments
  3. Implement enterprise customer success program to reduce churn risk
  4. Develop multi-year contracts with favorable terms

### Foreign Exchange Risk
**Risk Level: Low**
- **Risk**: International expansion exposes company to currency fluctuation risks.
- **Impact**: Revenue volatility, reduced margin predictability.
- **Mitigation Strategies**:
  1. Implement hedging strategy for major currency pairs
  2. Align expenses with revenue currency where possible
  3. Include currency adjustment clauses in long-term contracts
  4. Monitor exposure through regular financial reporting

## Risk Monitoring Framework

| Risk Category | Key Risk Indicators | Monitoring Frequency | Threshold for Action |
|--------------|---------------------|---------------------|----------------------|
| Market Adoption | Sales cycle length, POC conversion rate | Weekly | 25% deviation from target |
| Competitive | Win/loss ratio, Pricing pressure | Monthly | 15% adverse change |
| Technical | System availability, P1 incidents | Daily | <99.95% availability |
| Security | Vulnerability count, Mean time to patch | Weekly | Critical vulns >24hrs |
| Talent | Regretted attrition, Time to fill | Monthly | >10% quarterly attrition |
| Cash Flow | Runway months, CAC ratio | Weekly | <12 months runway |

## Contingency Plans

### Severe Market Headwinds
If market adoption significantly lags projections (>30% below target for two consecutive quarters):
1. Pivot to higher-demand segments or use cases
2. Reduce burn rate by 30% through prioritized cost cutting
3. Adjust pricing strategy and contract terms for faster adoption
4. Consider strategic options including potential acquisition

### Major Security Incident
In event of significant data breach or security incident:
1. Activate incident response team with clear roles and responsibilities
2. Implement pre-established communication plan (customers, regulators, public)
3. Engage third-party forensic specialists
4. Deploy customer retention program with compensation framework

### Competitive Disruption
If major competitor makes disruptive move (acquisition, pricing, or feature):
1. Conduct rapid competitive analysis and response planning
2. Accelerate relevant roadmap items
3. Implement targeted customer retention program
4. Consider strategic partnerships to strengthen market position`,
    ],
  },
};

export default function StretchableViewPresentation() {
  const [searchParams] = useSearchParams();
  const { shareId } = useParams();
  const navigate = useNavigate();
  const userPlan = useSelector((state: any) => state.user.userPlan);
  const isOutlineGenerated = useSelector(
    (state: any) => state.outline.isOutlineGenerated
  );
  const documentShareState = useSelector((state: any) => state.documentShare);
  const dispatch = useDispatch();

  // Get documentID from URL params or share params - MUST be declared first
  const documentID = shareId ? searchParams.get('docId') : searchParams.get('documentID');
  const sharedOrgId = shareId ? searchParams.get('orgId') : null;
  
  // Check if we're in share mode - only when accessing via share URL, not from history/edit
  const isShareMode = shareId && searchParams.get('docId') && searchParams.get('orgId');
  
  // Set document share state only if accessing via share URL
  React.useEffect(() => {
    if (shareId && documentID && sharedOrgId && isShareMode) {
      dispatch(setDocumentShare({
        docId: documentID,
        orgId: sharedOrgId,
        shareId: shareId
      }));
    }
  }, [shareId, documentID, sharedOrgId, dispatch, isShareMode]);
  const [pptName, setPptName] = useState<string | null>(null); // Changed to null initially

  const [isDocumentIDLoading, setIsDocumentIDLoading] = useState(false);
  const [isPptNameLoading, setIsPptNameLoading] = useState(true); // Start as loading
  const [currentSlide, setCurrentSlide] = useState(1);
  const [currentOutline, setCurrentOutline] = useState("Introduction");
  const [currentOutlineID, setCurrentOutlineID] = useState("outline1");
  const [outlineType, setOutlineType] = useState("Points");
  const [outlines, setOutlines] = useState<Outline[]>(
    sampleData.outlines as any
  );
  const [dynamicOutlines, setDynamicOutlines] = useState<any[]>([]);
  const [hasOutlineData, setHasOutlineData] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const orgId = sharedOrgId || sessionStorage.getItem("orgId") || "";
  const authToken = sessionStorage.getItem("authToken") || "";

  const [dropdownOpen, setDropdownOpen] = useState(false)
    const [newDropdownOpen, setNewDropdownOpen] = useState(false)
    const [monthlyPlan, setMonthlyPlan] = useState<Plan>()
    const [yearlyPlan, setYearlyPlan] = useState<Plan>()
    
    const [isPricingModalOpen, setIsPricingModalOpen] = useState(false)
    const userProfileImage = sessionStorage.getItem('userDP')
    const [currency, setCurrency] = useState<string>()
    const userProfileRef = useRef<HTMLDivElement | null>(null) // Reference to the profile image to handle click toggles
   
    const [subId, setSubId] = useState('')
    
    const dropdownRef = useRef<HTMLDivElement | null>(null)
    // Close dropdown when clicking outside of it
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        // Close user dropdown
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          userProfileRef.current &&
          !userProfileRef.current.contains(event.target as Node)
        ) {
          setDropdownOpen(false)
        }
        
        // Close new dropdown
        if (
          newDropdownRef.current &&
          !newDropdownRef.current.contains(event.target as Node)
        ) {
          setNewDropdownOpen(false)
        }
      }
  
      // Add event listener to document
      document.addEventListener('mousedown', handleClickOutside)
  
      // Cleanup the event listener when the component is unmounted or when dropdown state changes
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, []) // Empty dependency array ensures this effect runs once
  
    // API CALL TO GET PRICING DATA FOR MODAL - Skip for shared mode
    useEffect(() => {
      if (isShareMode) return; // Don't fetch pricing/user data for shared documents
      
      const getPricingData = async () => {
        const ipInfoResponse = await fetch(
          'https://api.zynth.ai/api/v1/data/userprofile/ip-info'
        )
        const ipInfoData: IpInfoResponse = await ipInfoResponse.json()
  
        await axios
          .get(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/payments/razorpay/plans`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          )
          .then((response) => {
            const country = ipInfoData!.country!
  
            if (country !== 'IN' && country !== 'India' && country !== 'In') {
              setMonthlyPlan(response.data.items[4])
              setYearlyPlan(response.data.items[2])
              setCurrency('USD')
            } else if (
              country === 'IN' ||
              country === 'India' ||
              country === 'In'
            ) {
              setMonthlyPlan(response.data.items[5])
              setYearlyPlan(response.data.items[3])
              setCurrency('INR')
            }
          })
      }
  
      const fetchUserPlan = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organization/${orgId}`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          )
          const subscriptionId = response.data.plan.subscriptionId
          setSubId(subscriptionId)
        } catch (error) {
          console.error('Error fetching user plan:', error)
        }
      }
  
      fetchUserPlan()
  
      const timer = setTimeout(() => {
        getPricingData()
      }, 3000) // delay
  
      // Cleanup the timer in case the component unmounts
      return () => clearTimeout(timer)
    }, [isShareMode])
    const monthlyPlanAmount = monthlyPlan?.item.amount! / 100
    const monthlyPlanId = monthlyPlan?.id
    const yearlyPlanAmount = yearlyPlan?.item.amount! / 100
    const yearlyPlanId = yearlyPlan?.id
  
    const toggleDropdown = () => {
      setDropdownOpen((prevState) => !prevState) // Toggle dropdown state
    }
  









  useEffect(() => {
    if (!scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible.length > 0) {
          const activeId = visible[0].target.getAttribute("data-outline-id");
          if (activeId && activeId !== currentOutlineID) {
            setCurrentOutlineID(activeId);
          }
        }
      },
      {
        root: scrollContainerRef.current,
        threshold: [0.3, 0.6, 1.0], // adjust sensitivity
      }
    );

    const sections =
      scrollContainerRef.current.querySelectorAll("[data-outline-id]");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [dynamicOutlines]);

  // Debug logging
  useEffect(() => {
    console.log("🔍 StretchableView state changed:", {
      isOutlineGenerated,
      hasOutlineData,
      documentID,
      dynamicOutlinesLength: dynamicOutlines.length,
      showOutline:
        (isOutlineGenerated || hasOutlineData) && dynamicOutlines.length > 0,
      showSkeleton: !(
        (isOutlineGenerated || hasOutlineData) &&
        dynamicOutlines.length > 0
      ),
    });
  }, [isOutlineGenerated, hasOutlineData, documentID, dynamicOutlines.length]);
  const [displayModes, setDisplayModes] = useState<{
    [key: string]: DisplayMode;
  }>({});
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slidesArray, setSlidesArray] = useState<{ [key: string]: string[] }>(
    sampleData.slides
  );
  const [displayBoxLoading, setDisplayBoxLoading] = useState(false);
  const [finalizedSlides, setFinalizedSlides] = useState<{
    [key: string]: string;
  }>({});

  // New state variables for the refine panel
  const [showRefinePanel, setShowRefinePanel] = useState(false);

  // Add useEffect to monitor showRefinePanel changes
  useEffect(() => {
    console.log("🔍 showRefinePanel state changed:", showRefinePanel);
    if (showRefinePanel) {
      toast.info("Opening refine panel");
    }
  }, [showRefinePanel]);
  const [selectedText, setSelectedText] = useState("");
  const [refineMode, setRefineMode] = useState<"rewrite" | "tone">("rewrite");

  // References
  const slideContainerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef<boolean>(false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(300); // Default width
  const [generatedData, setGeneratedData] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  
  // New navigation state variables
  const [showNewDropdown, setShowNewDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  
  
  const newDropdownRef = useRef<HTMLDivElement>(null);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  // Debug state changes
  useEffect(() => {
    console.log('showNewDropdown state changed:', showNewDropdown);
  }, [showNewDropdown]);

  useEffect(() => {
    console.log('showProfileDropdown state changed:', showProfileDropdown);
  }, [showProfileDropdown]);

  // Update user profile image from session storage
  // useEffect(() => {
  //   const profileImg = sessionStorage.getItem('userDP');
  //   console.log('Profile image from storage:', profileImg); // Debug log
  //   setUserProfileImage(profileImg);
    
  //   // Also listen for storage changes
  //   const handleStorageChange = () => {
  //     const updatedImg = sessionStorage.getItem('userDP');
  //     setUserProfileImage(updatedImg);
  //   };

  //   window.addEventListener('storage', handleStorageChange);
  //   return () => window.removeEventListener('storage', handleStorageChange);
  // }, []);

  // Function to add new outline item
  const addNewOutlineItem = (title: string, afterOutlineID: string) => {
    if (documentID && dynamicOutlines.length > 0) {
      const currentIndex = dynamicOutlines.findIndex(
        (outline) => outline.outlineID === afterOutlineID
      );
      if (currentIndex !== -1) {
        const newOutline = {
          outlineID: `outline_${Date.now()}`,
          title: title,
          type: "Text",
          position: currentIndex + 2,
        };
        const newOutlines = [...dynamicOutlines];
        newOutlines.splice(currentIndex + 1, 0, newOutline);
        setDynamicOutlines(newOutlines);
      }
    } else {
      const currentIndex = outlines.findIndex(
        (outline) => outline.outlineID === afterOutlineID
      );
      if (currentIndex !== -1) {
        const newOutline = {
          _id: `temp_${Date.now()}`, // Temporary _id until saved to backend
          outlineID: `outline_${Date.now()}`,
          title: title,
          type: "Text",
          position: currentIndex + 2,
        };
        const newOutlines = [...outlines];
        newOutlines.splice(currentIndex + 1, 0, newOutline);
        setOutlines(newOutlines as any);
      }
    }
  };

  // Function to delete outline item
  const deleteOutlineItem = (outlineID: string) => {
    if (documentID && dynamicOutlines.length > 0) {
      setDynamicOutlines((prev) =>
        prev.filter((outline) => outline.outlineID !== outlineID)
      );
    } else {
      setOutlines((prev) =>
        prev.filter((outline) => outline.outlineID !== outlineID)
      );
    }
    // If current outline is deleted, switch to first available outline
    if (outlineID === currentOutlineID) {
      const remainingOutlines =
        documentID && dynamicOutlines.length > 0
          ? dynamicOutlines.filter((outline) => outline.outlineID !== outlineID)
          : outlines.filter((outline) => outline.outlineID !== outlineID);
      if (remainingOutlines.length > 0) {
        setCurrentOutlineID(remainingOutlines[0].outlineID);
      }
    }
  };

  // Store all sections data for export
  const [allSectionsData, setAllSectionsData] = useState<{
    [key: string]: any[];
  }>({});

  // Update all sections data when generatedData changes
  useEffect(() => {
    if (documentID && currentOutlineID && generatedData.length > 0) {
      console.log(
        "Updating allSectionsData for outline:",
        currentOutlineID,
        "with data:",
        generatedData
      );
      setAllSectionsData((prev) => ({
        ...prev,
        [currentOutlineID]: generatedData,
      }));
    }
  }, [documentID, currentOutlineID, generatedData]);


 const fetchAllSectionsData = async () => {
  if (!documentID || !dynamicOutlines.length) return {};
  const API_URL =process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";
  const authToken = sessionStorage.getItem('authToken');
  const allData: {[key: string]: any[]} = {};

  for (const outline of dynamicOutlines) {
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/generatedData/${documentID}/${outline.outlineID}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      allData[outline.outlineID] = response.data?.generatedData || [];
    } catch (err) {
      allData[outline.outlineID] = [];
    }
  }
  return allData;
 };

  // Replace your exportToDocx function with this:
  const exportAllSectionsToDocx = async () => {
    setIsExporting(true);
    let htmlContent = "";

    if (documentID && dynamicOutlines.length > 0) {
      // Fetch all sections' data
      const allData = await fetchAllSectionsData();

      dynamicOutlines.forEach((outline) => {
        const sectionData = allData[outline.outlineID] || [];
        htmlContent += `<h1>${outline.title}</h1>`;
        sectionData.forEach((item) => {
          if (item.component_type === "Text" && item.text) {
            htmlContent += `<h2>${item.text.title}</h2><p>${item.text.text}</p>`;
          }
          if (item.component_type === "People" && item.people) {
            htmlContent += `<h2>${item.people.title}</h2><p>${item.people.overview}</p>`;
          }
          if (item.component_type === "Table" && item.table) {
            htmlContent += `<h2>${item.table.title}</h2>`;
            if (Array.isArray(item.table.rows) && item.table.headers) {
              htmlContent +=
                '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
              htmlContent +=
                "<tr>" +
                item.table.headers
                  .map(
                    (h: string) =>
                      `<th style="padding: 8px; background-color: #f0f0f0;">${h}</th>`
                  )
                  .join("") +
                "</tr>";
              item.table.rows.forEach((row: any[]) => {
                htmlContent +=
                  "<tr>" +
                  row
                    .map(
                      (cell) => `<td style="padding: 8px;">${cell || ""}</td>`
                    )
                    .join("") +
                  "</tr>";
              });
              htmlContent += "</table>";
            }
          }
          if (item.component_type === "Chart" && item.chart) {
            htmlContent += `<h2>${item.chart.title}</h2><p>${item.chart.overview}</p>`;
          }
          if (item.component_type === "TextandImage" && item.textandimage) {
            htmlContent += `<h2>${item.textandimage.title}</h2><p>${item.textandimage.text}</p>`;
          }
          // Add other component types as needed
        });
        htmlContent += `<div style="page-break-before: always;"></div>`;
      });
    } else {
      // Fallback: export sample slides
      const allSlides = Object.entries(slidesArray)
        .map(([outlineId, slides]) => {
          const outlineTitle =
            outlines.find((o) => o.outlineID === outlineId)?.title ||
            "Untitled Section";
          const slideContent = slides[0] || "No content available";
          return `<h1>${outlineTitle}</h1>${slideContent.replace(
            /\n/g,
            "<br/>"
          )}`;
        })
        .join('<div style="page-break-before: always;"></div>');
      htmlContent = allSlides;
    }

    if (!htmlContent.trim()) {
      alert("No content available to export.");
      setIsExporting(false);
      return;
    }

    const cleanHtml = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${pptName || "presentation"}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 40px; }
        h1 { color: #2c3e50; margin-bottom: 20px; }
        h2 { color: #34495e; margin-top: 30px; margin-bottom: 15px; }
        p { line-height: 1.6; margin-bottom: 15px; }
        table { border-collapse: collapse; width: 100%; margin: 15px 0; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
      </style>
    </head>
    <body>
      ${htmlContent}
    </body>
    </html>
  `;

    const docx = HtmlDocx.asBlob(cleanHtml);
    saveAs(docx, `${pptName || "presentation"}.docx`);
    setIsExporting(false);
  };

  // Export function that handles all component types
  const exportToDocx = () => {
    console.log("=== EXPORT DEBUG START ===");
    console.log("documentID:", documentID);
    console.log("currentOutlineID:", currentOutlineID);
    console.log("generatedData length:", generatedData.length);
    console.log("generatedData:", generatedData);

    setIsExporting(true);

    let htmlContent = "";

    if (documentID && generatedData.length > 0) {
      const currentOutlineTitle =
        dynamicOutlines.find((o) => o.outlineID === currentOutlineID)?.title ||
        "Current Section";
      console.log("currentOutlineTitle:", currentOutlineTitle);

      const itemsHtml = generatedData
        .map((item: any, index: number) => {
          console.log(`Processing item ${index}:`, item);
          console.log(`Item component_type:`, item.component_type);

          // Handle Text components
          if (item.component_type === "Text" && item.text) {
            const textContent =
              item.text.text || item.text || "No text content";
            const textTitle = item.text.title || "Text Content";
            console.log(
              `Text - Title: "${textTitle}", Content: "${textContent}"`
            );
            return `<h2>${textTitle}</h2><p>${textContent}</p>`;
          }

          // Handle People components
          if (item.component_type === "People" && item.people) {
            const peopleTitle = item.people.title || "People";
            const peopleText = item.people.text || "No people information";
            console.log(
              `People - Title: "${peopleTitle}", Text: "${peopleText}"`
            );
            return `<h2>${peopleTitle}</h2><p>${peopleText}</p>`;
          }

          // Handle Table components
          if (item.component_type === "Table" && item.table) {
            const tableTitle = item.table.title || "Table";
            let tableHtml = `<h2>${tableTitle}</h2>`;

            // Check for structured table data
            if (item.table.columnHeader1) {
              const headers = [
                item.table.columnHeader1,
                item.table.columnHeader2,
                item.table.columnHeader3,
                item.table.columnHeader4,
                item.table.columnHeader5,
              ].filter(Boolean);
              const rows = [];

              for (let i = 1; i <= 8; i++) {
                const rowHeader = item.table[`rowHeader${i}`];
                const rowData = item.table[`rows${i}`];
                if (rowHeader && rowData) {
                  const rowValues = [
                    rowData.attribute1,
                    rowData.attribute2,
                    rowData.attribute3,
                    rowData.attribute4,
                    rowData.attribute5,
                  ].filter(Boolean);
                  rows.push([rowHeader, ...rowValues]);
                }
              }

              if (headers.length > 0 && rows.length > 0) {
                tableHtml +=
                  '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
                tableHtml +=
                  "<tr>" +
                  headers
                    .map(
                      (h) =>
                        `<th style="padding: 8px; background-color: #f0f0f0;">${h}</th>`
                    )
                    .join("") +
                  "</tr>";
                rows.forEach((row) => {
                  tableHtml +=
                    "<tr>" +
                    row
                      .map(
                        (cell) => `<td style="padding: 8px;">${cell || ""}</td>`
                      )
                      .join("") +
                    "</tr>";
                });
                tableHtml += "</table>";
              }
            }

            console.log(`Table - Title: "${tableTitle}"`);
            return tableHtml;
          }

          // Handle Chart components
          if (item.component_type === "Chart" && item.chart) {
            const chartTitle = item.chart.title || "Chart";
            const chartOverview = item.chart.overview || "No chart description";
            console.log(
              `Chart - Title: "${chartTitle}", Overview: "${chartOverview}"`
            );
            return `<h2>${chartTitle}</h2><p>${chartOverview}</p>`;
          }

          // Handle TextandImage components
          if (item.component_type === "TextandImage" && item.textandimage) {
            const title = item.textandimage.title || "Text and Image";
            const text = item.textandimage.text || "No text content";
            console.log(`TextandImage - Title: "${title}", Text: "${text}"`);
            return `<h2>${title}</h2><p>${text}</p>`;
          }

          // Fallback for unknown components
          console.log(`Unknown component type: ${item.component_type}`);
          return `<h2>Unknown Component (${
            item.component_type
          })</h2><pre>${JSON.stringify(item, null, 2)}</pre>`;
        })
        .join("");

      htmlContent = `<h1>${currentOutlineTitle}</h1>${itemsHtml}`;
      console.log("Generated itemsHtml:", itemsHtml);
    } else {
      console.log("Export conditions not met:");
      console.log("- documentID exists:", !!documentID);
      console.log("- generatedData has items:", generatedData.length > 0);

      if (!documentID) {
        // Export sample slide content
        const allSlides = Object.entries(slidesArray)
          .map(([outlineId, slides]) => {
            const outlineTitle =
              outlines.find((o) => o.outlineID === outlineId)?.title ||
              "Untitled Section";
            const slideContent = slides[0] || "No content available";
            return `<h1>${outlineTitle}</h1>${slideContent.replace(
              /\n/g,
              "<br/>"
            )}`;
          })
          .join('<div style="page-break-before: always;"></div>');
        htmlContent = allSlides;
      } else {
        htmlContent =
          "<h1>No Data Available</h1><p>Navigate to sections with data to export them.</p>";
      }
    }

    if (!htmlContent || htmlContent.trim() === "") {
      console.warn("No content to export");
      alert(
        "No content available to export. Please navigate through the sections to load data first."
      );
      setIsExporting(false);
      return;
    }

    console.log("Final HTML content:", htmlContent);
    console.log("Final HTML content length:", htmlContent.length);

    // Create a more compatible HTML structure for DOCX conversion
    const cleanHtml = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <title>${pptName || "presentation"}</title>
        <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            h1 { color: #2c3e50; margin-bottom: 20px; }
            h2 { color: #34495e; margin-top: 30px; margin-bottom: 15px; }
            p { line-height: 1.6; margin-bottom: 15px; }
            table { border-collapse: collapse; width: 100%; margin: 15px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
        </style>
    </head>
    <body>
        ${htmlContent}
    </body>
    </html>`;

    console.log("Clean HTML for DOCX:", cleanHtml);

    try {
      const docx = HtmlDocx.asBlob(cleanHtml);
      saveAs(docx, `${pptName || "presentation"}.docx`);
      console.log("DOCX file saved successfully");
    } catch (error) {
      console.error("Error creating DOCX:", error);
      // Fallback: save as HTML file
      const htmlBlob = new Blob([cleanHtml], { type: "text/html" });
      saveAs(htmlBlob, `${pptName || "presentation"}.html`);
      console.log("Saved as HTML file instead");
    }

    setIsExporting(false);
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";
  // Helper function for refine action - now we're using inline refine panels in SectionRenderer
  const handleRefineClick = (itemText?: string) => {
    console.log("🔄 Refine button clicked!");

    // Get selected text from the slide or use the provided text from the item
    let textToRefine = window.getSelection()?.toString() || "";

    // If we have text from the component and no text is selected, use the component text
    if (itemText && textToRefine === "") {
      textToRefine = itemText;
    }

    console.log("📝 Text to refine:", textToRefine);
    setSelectedText(textToRefine);

    // Store in session storage for the refine panel to use
    sessionStorage.setItem("selectedSlideText", textToRefine);

    // We no longer need to set these in the parent component as we're using inline panels
    // The SectionRenderer component will handle its own refine panel state
    // setRefineMode("rewrite");
    // setShowRefinePanel(true);
    console.log("✅ Text selected for refinement");
  };

  const handleDeleteClick = async (outlineID: string, type: string) => {
    const payLoad = {
      outlineID: outlineID,
      type: type,
      documentID: documentID,
    };

    try {
      const res = await axios.post(
        `${API_URL}/api/v1/data/generatedocument/delete-section/${orgId}`,
        payLoad,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.status === 200) {
        toast.success("Section deleted successfully");
        fetchOutlineData();
        dispatch(setIsOutlineGenerated(false));
      } else {
        toast.error("Failed to delete section");
      }
    } catch (error) {
      toast.error("Failed to delete section");
      console.error("Error deleting section:", error);
    }
  };

  // Scroll the sidebar to the selected outline when it changes
  useEffect(() => {
    if (currentOutlineID && sidebarRef.current) {
      // Add a small delay to ensure the DOM is updated
      setTimeout(() => {
        // Find the selected outline element by its ID
        const selectedElement = document.querySelector(
          `li[data-outline-id="${currentOutlineID}"]`
        );
        if (selectedElement) {
          // Always scroll the sidebar to keep the selected item visible
          // This is safe because it only affects the sidebar, not the main content
          selectedElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 50);
    }
  }, [currentOutlineID]);

  // Handle resize functionality
  useEffect(() => {
    const resizeElement = resizeRef.current;
    if (!resizeElement) return;

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      isResizingRef.current = true;
      document.body.style.cursor = "col-resize";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;

      const newWidth = e.clientX;
      if (newWidth > 200 && newWidth < window.innerWidth * 0.5) {
        setSidebarWidth(newWidth);
      }
    };

    const onMouseUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = "default";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    resizeElement.addEventListener("mousedown", onMouseDown);

    return () => {
      resizeElement.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Initialize display modes
  useEffect(() => {
    const initialDisplayModes: { [key: string]: DisplayMode } = {};
    outlines.forEach((outline) => {
      initialDisplayModes[outline.outlineID] = "slides";
    });
    setDisplayModes(initialDisplayModes);
  }, [outlines]);
  
  // Fetch document name function
  const fetchDocumentName = useCallback(async () => {
    if (!documentID) {
      setPptName("Sample Presentation"); // Fallback for no documentID
      setIsPptNameLoading(false);
      return;
    }
    
    setIsPptNameLoading(true);
    const authToken = sessionStorage.getItem('authToken');
    
    // Use REACT_APP_BACKEND_URL environment variable like other parts of the app
    const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
    
    try {
      // For shared documents, use public endpoint
      const headers = isShareMode ? {} : { Authorization: `Bearer ${authToken}` };
      const endpoint = isShareMode 
        ? `${backendUrl}/api/v1/data/documents/shared/${shareId}/name`
        : `${backendUrl}/api/v1/data/appscripts/document-name/${documentID}`;
      
      let response;
      if (isShareMode) {
        response = await axios.get(
          `${backendUrl}/api/v1/data/documents/shared/${shareId}/name`
        );
      } else {
        response = await axios.get(
          `${backendUrl}/api/v1/data/appscripts/document-name/${documentID}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
      }
      
      if (response.data.success) {
        setPptName(response.data.documentName);
      } else {
        setPptName("Untitled Presentation");
      }
    } catch (err) {
      console.error('Failed to fetch document name:', err);
      setPptName("Untitled Presentation"); // Fallback on error
    } finally {
      setIsPptNameLoading(false);
    }
  }, [documentID]);
  

  // Memoized fetch function
  const fetchOutlineData = useCallback(async (retryCount = 0) => {
    if (!documentID) return;

    console.log("🔍 fetchOutlineData called for documentID:", documentID, "retry:", retryCount);

    try {
      const authToken = sessionStorage.getItem('authToken'); 
      const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001'
      let response;
      if (isShareMode) {
        response = await axios.get(
          `${backendUrl}/api/v1/outline/shared/${shareId}/documentGenerationOutline`
        );
      } else {
        response = await axios.get(
          `${backendUrl}/api/v1/outline/${documentID}/documentGenerationOutline`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
      }

      console.log("📡 API Response:", response.data);

      if (response.data?.outline && response.data.outline.length > 0) {
        console.log("✅ Setting dynamicOutlines with:", response.data.outline);
        const newOutlines = response.data.outline;
        
        // Preserve current selection if it still exists
        const currentExists = newOutlines.find(
          (o: { outlineID: string }) => o.outlineID === currentOutlineID
        );
        
        setDynamicOutlines(newOutlines);
        
        if (!currentExists && newOutlines.length > 0) {
          setCurrentOutlineID(newOutlines[0].outlineID);
          setCurrentOutline(newOutlines[0].title);
        }
        
        setHasOutlineData(true);
        console.log("✅ hasOutlineData set to true");
      } else {
        console.log("❌ No outline data found");
        // If no data and this is first retry, try again after a short delay
        if (retryCount < 2) {
          setTimeout(() => fetchOutlineData(retryCount + 1), 2000);
          return;
        }
        setHasOutlineData(false);
      }
    } catch (error) {
      console.error("❌ Error fetching outline data:", error);
      // If error and this is first retry, try again after a short delay
      if (retryCount < 2) {
        setTimeout(() => fetchOutlineData(retryCount + 1), 2000);
        return;
      }
      setHasOutlineData(false);
    }
  }, [documentID, currentOutlineID]);

  // Check for existing outline data on page load/reload
  useEffect(() => {
    if (documentID) {
      fetchOutlineData();
    }
  }, [documentID, fetchOutlineData]);

  // Listen for outline generation completion
  useEffect(() => {
    if (isOutlineGenerated) {
      console.log("✅ Outline generated successfully, fetching outlines...");
      console.log(
        "🔄 Current state - isOutlineGenerated:",
        isOutlineGenerated,
        "hasOutlineData:",
        hasOutlineData,
        "dynamicOutlines.length:",
        dynamicOutlines.length
      );
      fetchOutlineData();
      dispatch(setIsOutlineGenerated(false));
    }
  }, [
    isOutlineGenerated,
    fetchOutlineData,
    hasOutlineData,
    dynamicOutlines.length,
    dispatch,
  ]);

  const [lockHighlight, setLockHighlight] = useState(false);
  const lockRef = useRef<boolean>(false);
  const lockTimeoutRef = useRef<number | null>(null);
  const scrollEndTimeoutRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null); 


  // Fetch document name when component mounts or documentID changes
  useEffect(() => {
    fetchDocumentName();
  }, [fetchDocumentName]);


  // Enhanced scroll-based outline highlighting with component stability
  useEffect(() => {
    const sections = Array.from(document.querySelectorAll<HTMLElement>(".scroll-section"));
    if (!sections.length) return;

    // Find the scrollable parent for sections (fallback to window)
    const findScrollableParent = (el: HTMLElement | null): HTMLElement | Window => {
      let parent = el?.parentElement ?? null;
      while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        if (/(auto|scroll)/.test(style.overflowY)) return parent;
        parent = parent.parentElement;
      }
      return window;
    };

    const scrollContainer = findScrollableParent(sections[0]);
    const getViewportCenter = () => (scrollContainer === window ? window.innerHeight / 2 : (scrollContainer as HTMLElement).clientHeight / 2);
    const getContainerTop = () => (scrollContainer === window ? 0 : (scrollContainer as HTMLElement).getBoundingClientRect().top);

    // Track component addition state to prevent flickering
    let isAddingComponent = false;
    let addComponentTimeout: NodeJS.Timeout | null = null;

    const computeActive = () => {
      rafRef.current = null;

      // Skip computation if components are being added
      if (isAddingComponent) return;

      const scrollTop =
        scrollContainer === window
          ? window.scrollY || document.documentElement.scrollTop
          : (scrollContainer as HTMLElement).scrollTop;

      const maxScroll =
        scrollContainer === window
          ? document.documentElement.scrollHeight - window.innerHeight
          : (scrollContainer as HTMLElement).scrollHeight -
            (scrollContainer as HTMLElement).clientHeight;

      // Edge case: at very top → force first section
      if (scrollTop <= 10) {
        const first = sections[0];
        if (first) {
          const firstId = first.getAttribute("data-outline-id")!;
          if (!lockRef.current && firstId !== currentOutlineID) {
            const outline = dynamicOutlines.find((o: any) => o.outlineID === firstId);
            if (outline) {
              setCurrentOutlineID(firstId);
              setCurrentOutline(outline.title);
            }
          }
        }
        return;
      }

      // Edge case: at very bottom → force last section
      if (scrollTop + 10 >= maxScroll) {
        const last = sections[sections.length - 1];
        if (last) {
          const lastId = last.getAttribute("data-outline-id")!;
          if (!lockRef.current && lastId !== currentOutlineID) {
            const outline = dynamicOutlines.find((o: any) => o.outlineID === lastId);
            if (outline) {
              setCurrentOutlineID(lastId);
              setCurrentOutline(outline.title);
            }
          }
        }
        return;
      }

      // Normal case: pick section closest to center with enhanced stability
      let bestId: string | null = null;
      let bestDist = Number.POSITIVE_INFINITY;
      const viewportCenter = getViewportCenter();
      const topOffset = getContainerTop();
      const stabilityThreshold = 80; // Increased threshold for better stability

      for (const sec of sections) {
        const rect = sec.getBoundingClientRect();
        // Only consider sections that are at least partially visible
        if (rect.bottom < topOffset || rect.top > topOffset + (scrollContainer === window ? window.innerHeight : (scrollContainer as HTMLElement).clientHeight)) {
          continue;
        }
        
        const center = rect.top + rect.height / 2;
        const dist = Math.abs(center - (topOffset + viewportCenter));
        if (dist < bestDist) {
          bestDist = dist;
          bestId = sec.getAttribute("data-outline-id");
        }
      }

      // Enhanced change logic with hysteresis
      if (bestId && !lockRef.current && bestId !== currentOutlineID) {
        const currentSection = sections.find(s => s.getAttribute("data-outline-id") === currentOutlineID);
        let shouldChange = true;
        
        if (currentSection) {
          const currentRect = currentSection.getBoundingClientRect();
          const currentCenter = currentRect.top + currentRect.height / 2;
          const currentDist = Math.abs(currentCenter - (topOffset + viewportCenter));
          
          // Only change if new section is significantly closer AND current section is not well-centered
          shouldChange = bestDist + stabilityThreshold < currentDist && currentDist > 100;
        }
        
        if (shouldChange) {
          const outline = dynamicOutlines.find((o: any) => o.outlineID === bestId);
          if (outline) {
            setCurrentOutlineID(bestId);
            setCurrentOutline(outline.title);
          } else {
            setCurrentOutlineID(bestId);
          }
        }
      }
    };

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(computeActive);

      // Release lock after user stops scrolling with longer delay for stability
      if (scrollEndTimeoutRef.current) window.clearTimeout(scrollEndTimeoutRef.current);
      scrollEndTimeoutRef.current = window.setTimeout(() => {
        lockRef.current = false;
        isAddingComponent = false; // Reset component addition flag
      }, 800); // Increased delay
    };

    // Listen for component addition events to prevent flickering
    const handleComponentAdded = () => {
      isAddingComponent = true;
      if (addComponentTimeout) clearTimeout(addComponentTimeout);
      addComponentTimeout = setTimeout(() => {
        isAddingComponent = false;
      }, 1500); // Prevent outline changes for 1.5s after component addition
    };

    // Listen for DOM mutations that indicate component additions
    const mutationObserver = new MutationObserver((mutations) => {
      let hasNewComponents = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.classList?.contains('scroll-section') || 
                  element.querySelector?.('.scroll-section') ||
                  element.closest?.('.scroll-section')) {
                hasNewComponents = true;
              }
            }
          });
        }
      });
      
      if (hasNewComponents) {
        handleComponentAdded();
      }
    });

    // Observe the main content area for component additions
    const contentArea = document.querySelector('.data-list') || document.body;
    mutationObserver.observe(contentArea, {
      childList: true,
      subtree: true
    });

    // Attach scroll listener to correct target
    if (scrollContainer === window) {
      window.addEventListener("scroll", onScroll, { passive: true });
    } else {
      (scrollContainer as HTMLElement).addEventListener("scroll", onScroll, { passive: true });
    }

    // Initial run with delay to allow DOM to settle
    setTimeout(computeActive, 100);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (scrollEndTimeoutRef.current) window.clearTimeout(scrollEndTimeoutRef.current);
      if (lockTimeoutRef.current) window.clearTimeout(lockTimeoutRef.current);
      if (addComponentTimeout) clearTimeout(addComponentTimeout);
      
      mutationObserver.disconnect();
      
      if (scrollContainer === window) {
        window.removeEventListener("scroll", onScroll);
      } else {
        (scrollContainer as HTMLElement).removeEventListener("scroll", onScroll);
      }
    };
  }, [dynamicOutlines, currentOutlineID]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if click is outside new dropdown and not on the button itself
      if (newDropdownRef.current && !newDropdownRef.current.contains(target)) {
        const isNewButton = target.closest('[data-dropdown="new"]');
        if (!isNewButton) {
          setShowNewDropdown(false);
        }
      }
      
      // Check if click is outside profile dropdown and not on the button itself
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(target)) {
        const isProfileButton = target.closest('[data-dropdown="profile"]');
        if (!isProfileButton) {
          setShowProfileDropdown(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  // Enhanced outline selection with component stability
  const handleOutlineSelect = (
    outlineTitle: string,
    outlineID: string,
    outlineType: string,
    isUserClick: boolean = true
  ) => {
    setCurrentOutline(outlineTitle);
    setCurrentOutlineID(outlineID);
    setOutlineType(outlineType);
    setCurrentSlideIndex(0);
    setLockHighlight(true);

    setTimeout(() => setLockHighlight(false), 800); // Increased timeout
    console.log(outlineID)

    // Only scroll if this is a user-initiated click
    if (isUserClick) {
      // Lock scroll-based outline changes during user navigation
      lockRef.current = true;
      if (lockTimeoutRef.current) window.clearTimeout(lockTimeoutRef.current);
      lockTimeoutRef.current = window.setTimeout(() => {
        lockRef.current = false;
      }, 2000); // Increased lock duration

      // Enhanced scroll to section with better positioning
      setTimeout(() => {
        // Try desktop section first, then mobile section
        let sectionElement = document.getElementById(`section-${outlineID}`);
        if (!sectionElement) {
          sectionElement = document.getElementById(`mobile-section-${outlineID}`);
        }

        // Also try data-outline-id selector as fallback
        if (!sectionElement) {
          sectionElement = document.querySelector(`[data-outline-id="${outlineID}"]`) as HTMLElement;
        }

        if (sectionElement) {
          // Use more precise scrolling with offset for better positioning
          const rect = sectionElement.getBoundingClientRect();
          const scrollContainer = sectionElement.closest('.overflow-y-auto') || window;
          
          if (scrollContainer === window) {
            const offsetTop = sectionElement.offsetTop - 100; // 100px offset from top
            window.scrollTo({ 
              top: Math.max(0, offsetTop), 
              behavior: "smooth" 
            });
          } else {
            const containerElement = scrollContainer as HTMLElement;
            const containerRect = containerElement.getBoundingClientRect();
            const relativeTop = rect.top - containerRect.top + containerElement.scrollTop - 50;
            
            containerElement.scrollTo({
              top: Math.max(0, relativeTop),
              behavior: "smooth"
            });
          }
        }
      }, 150); // Slightly increased delay for DOM stability
    }
  };

  // Paginate Next
  const handlePaginateNext = () => {
    const currentSlides = slidesArray[currentOutlineID];
    if (currentSlides && currentSlideIndex < currentSlides.length - 1) {
      setCurrentSlideIndex((prev) => prev + 1);
    }
  };

  // Paginate Back
  const handlePaginatePrev = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex((prev) => prev - 1);
    }
  };

  // New handler functions for navigation dropdowns
  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/');
  };

  const toggleNewDropdown = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('toggleNewDropdown called, current state:', showNewDropdown);
    const newState = !showNewDropdown;
    setShowNewDropdown(newState);
    if (newState) {
      setShowProfileDropdown(false);
    }
    console.log('Setting showNewDropdown to:', newState);
  };

  const toggleProfileDropdown = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('toggleProfileDropdown called, current state:', showProfileDropdown);
    const newState = !showProfileDropdown;
    setShowProfileDropdown(newState);
    if (newState) {
      setShowNewDropdown(false);
    }
    console.log('Setting showProfileDropdown to:', newState);
  };

  // Function to convert markdown-like content to HTML elements
  const renderMarkdown = (content: string) => {
    if (!content) return <p>No content available</p>;

    // Split the content by lines
    const lines = content.split("\n");
    const result = [];

    // Process the lines to handle markdown-like syntax
    let i = 0;
    let tableStarted = false;
    let tableRows: React.ReactNode[] = [];

    while (i < lines.length) {
      const line = lines[i];

      // Headers
      if (line.startsWith("# ")) {
        result.push(
          <h1 key={`h1-${i}`} className="text-3xl font-bold mt-6 mb-4">
            {line.substring(2)}
          </h1>
        );
      } else if (line.startsWith("## ")) {
        result.push(
          <h2 key={`h2-${i}`} className="text-2xl font-bold mt-5 mb-3">
            {line.substring(3)}
          </h2>
        );
      } else if (line.startsWith("### ")) {
        result.push(
          <h3 key={`h3-${i}`} className="text-xl font-bold mt-4 mb-2">
            {line.substring(4)}
          </h3>
        );
      }

      // Lists
      else if (line.match(/^\d+\. /)) {
        const content = line.replace(/^\d+\. /, "");
        result.push(
          <div key={`ol-${i}`} className="flex ml-5 my-1">
            <span className="mr-2 text-blue-600 font-bold">•</span>
            <span>{content}</span>
          </div>
        );
      } else if (line.startsWith("- ")) {
        result.push(
          <div key={`ul-${i}`} className="flex ml-5 my-1">
            <span className="mr-2 text-blue-600">•</span>
            <span>{line.substring(2)}</span>
          </div>
        );
      }

      // Tables
      else if (line.includes("|")) {
        if (!tableStarted) {
          tableStarted = true;
          tableRows = [];
        }

        const cells = line.split("|").filter((cell) => cell !== "");

        if (line.includes("---")) {
          // This is a separator row, we can skip it
          i++;
          continue;
        }

        // Add this as a table row
        tableRows.push(
          <tr key={`tr-${i}`} className={i % 2 === 0 ? "bg-gray-50" : ""}>
            {cells.map((cell, cellIndex) => (
              <td key={`td-${i}-${cellIndex}`} className="border px-4 py-2">
                {cell.trim()}
              </td>
            ))}
          </tr>
        );

        // Check if the next line is not part of the table
        if (i === lines.length - 1 || !lines[i + 1].includes("|")) {
          result.push(
            <div key={`table-container-${i}`} className="my-4 overflow-x-auto">
              <table
                key={`table-${i}`}
                className="min-w-full border border-gray-300 rounded"
              >
                <tbody>{tableRows}</tbody>
              </table>
            </div>
          );
          tableStarted = false;
        }
      }

      // Empty lines as spacers
      else if (line.trim() === "") {
        if (!tableStarted) {
          result.push(<div key={`space-${i}`} className="h-4"></div>);
        }
      }

      // Default: regular paragraph
      else {
        if (!tableStarted) {
          result.push(
            <p key={`p-${i}`} className="my-2">
              {line}
            </p>
          );
        }
      }

      i++;
    }

    return result;
  };

  // Render slide content
  const renderSlideContent = (outlineID: string, slideIndex: number) => {
    const slideContent = slidesArray[outlineID]?.[slideIndex];

    return (
      <div className="w-full h-auto border border-gray-300 rounded-md bg-white shadow-sm">
        <div className="p-4 md:p-8">
          <h3 className="text-2xl font-bold mb-4 md:mb-6 text-center">
            {currentOutline}
          </h3>
          <div className="prose max-w-none">
            {renderMarkdown(slideContent || "No content available")}
          </div>

          {/* Adding padding at the bottom to ensure content is not cut off */}
          <div className="h-8"></div>
        </div>
      </div>
    );
  };

  // Render mobile view
  const renderMobileView = () => {
    return (
      <>
      <div className="flex flex-col w-full h-full overflow-hidden">
        <div className="p-3 bg-white border-b border-gray-200">
          {/* Mobile Layout: Buttons first, then title */}
          <div className="block sm:hidden">
            <div className="flex items-center mb-2 px-2 gap-2 justify-between max-w-full">
              {/* Left side buttons */}
              {!isShareMode && (
                <div className="flex items-center space-x-2">
                  {/* Export Button */}
                  <div className="flex-shrink-0">
                    <ExportPresentationButton documentID={documentID} pptName={pptName} outlines={dynamicOutlines.length ? dynamicOutlines : outlines} />
                  </div>
                  
                  {/* Share Button */}
                  {documentID && (
                    <div className="flex-shrink-0">
                      <DocumentShareButton 
                        documentID={documentID} 
                        orgId={orgId} 
                        documentName={pptName || 'Document'} 
                      />
                    </div>
                  )}
                  
                  {/* New Presentation Button */}
                  <button
                    onClick={() => navigate('/new-presentation')}
                    className="bg-[#3667B2] border border-[#3667B2] text-white hover:bg-white hover:text-[#3667B2] hover:border-[#3667B2] text-base font-medium px-2 py-2 rounded-md active:scale-95 transition transform duration-300"
                  >
                    <FaChartBar className="w-4 h-4" />
                  </button>

                  {/* New Document Button */}
                  <button
                    onClick={() => navigate('/new-document')}
                    className="bg-[#3667B2] border border-[#3667B2] text-white hover:bg-white hover:text-[#3667B2] hover:border-[#3667B2] text-base font-medium px-2 py-2 rounded-md active:scale-95 transition transform duration-300"
                  >
                    <FaFileAlt className="w-4 h-4" />
                  </button>

                  {/* History Button */}
                  <button
                    onClick={() => navigate('/history')}
                    className="bg-white border-[#3667B2] border text-[#3667B2] hover:bg-[#3667B2] hover:text-white hover:border-[#3667B2] text-base font-medium px-2 py-2 rounded-md active:scale-95 transition transform duration-300"
                  >
                    <FaClock className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* User Profile - Right side */}
              {!isShareMode && (
                <div
                  id={'organization-profile'}
                  ref={userProfileRef}
                  className="flex items-center justify-center flex-shrink-0 relative"
                >
                  {userProfileImage ? (
                    <img
                      src={userProfileImage}
                      alt="User Profile"
                      className="w-8 h-8 rounded-full hover:scale-105 cursor-pointer"
                      onClick={toggleDropdown}
                      title="User Profile"
                    />
                  ) : (
                    <FaUser
                      className="w-6 h-6 text-gray-600 hover:text-gray-800 cursor-pointer"
                      onClick={toggleDropdown}
                      title="User Profile"
                    />
                  )}
                  {/* Dropdown */}
                  {dropdownOpen && (
                    <div
                      className="fixed top-16 right-4 bg-white shadow-xl rounded-md p-2 w-48 h-auto border border-gray-200"
                      style={{ zIndex: 2147483647, position: 'fixed' }}
                      onClick={() => setDropdownOpen(false)}
                    >
                      <button
                        onClick={() => navigate('/organization-profile')}
                        className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                      >
                        Organization Profile
                      </button>
                      <button
                        onClick={() => openPricing('header')}
                        className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                      >
                        Subscription Plans
                      </button>
                      <button
                       onClick={() => navigate('/refer')}
                        className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                      >
                        Refer & Earn
                      </button>
                      <button onClick={() => navigate('/invoice')}
                        className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                    >
                        Invoices
                      </button>
        
                      <button
                        className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Pricing modal removed — redirect to external pricing page instead */}


            </div>
            
            {/* Mobile Title - Second Row */}
            <h1 className="text-xl font-bold">
              {isPptNameLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-pulse bg-gray-300 h-6 w-48 rounded"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                pptName || "Sample Presentation"
              )}
            </h1>
          </div>

          {/* Desktop Layout: Title and buttons side by side */}
          <div className="hidden sm:flex justify-between items-center mb-2">
            <h1 className="text-2xl font-bold">
              {isPptNameLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-pulse bg-gray-300 h-6 w-48 rounded"></div>
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              ) : (
                pptName || "Sample Presentation"
              )}
            </h1>
            {!isShareMode && (
              <div className="flex items-center space-x-2">
                {/* Export Button - First */}
                <ExportPresentationButton documentID={documentID} pptName={pptName} outlines={dynamicOutlines.length ? dynamicOutlines : outlines} />
                
                {/* Share Button */}
                {documentID && (
                  <DocumentShareButton 
                    documentID={documentID} 
                    orgId={orgId} 
                    documentName={pptName || 'Document'} 
                  />
                )}
                
                {/* New Presentation Button */}
                <div className="relative group">
                  <button
                    onClick={() => navigate('/new-presentation')}
                    className="bg-[#3667B2] border border-[#3667B2] lg:h-[2.5rem] text-white hover:bg-white hover:text-[#3667B2] hover:border-[#3667B2] text-sm lg:text-base font-medium px-2 sm:px-3 py-2 lg:py-2 rounded-md active:scale-95 transition transform duration-300"
                  >
                    <span className="md:hidden text-base">
                      <FaChartBar />
                    </span>
                    <span className="hidden md:flex items-center space-x-2 text-sm lg:text-base">
                      <FaChartBar />
                      <span>New Presentation</span>
                    </span>
                  </button>
                  {/* Tooltip */}
                  <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-max max-w-xs text-center z-50">
                    Start a new AI presentation with slides and visuals.
                  </div>
                </div>

                {/* New Document Button */}
                <div className="relative group">
                  <button
                    onClick={() => navigate('/new-document')}
                    className="bg-[#3667B2] border border-[#3667B2] lg:h-[2.5rem] text-white hover:bg-white hover:text-[#3667B2] hover:border-[#3667B2] text-sm lg:text-base font-medium px-2 sm:px-3 py-2 lg:py-2 rounded-md active:scale-95 transition transform duration-300"
                  >
                    <span className="md:hidden text-base">
                      <FaFileAlt />
                    </span>
                    <span className="hidden md:flex items-center space-x-2 text-sm lg:text-base">
                      <FaFileAlt />
                      <span>New Document</span>
                    </span>
                  </button>
                  {/* Tooltip */}
                  <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-max max-w-xs text-center z-50">
                    Start a new AI document like a report, essay, or proposal.
                  </div>
                </div>

                {/* History Button */}
                <button
                  onClick={() => navigate('/history')}
                  className="bg-white lg:h-[2.5rem] border-[#3667B2] border text-[#3667B2] hover:bg-[#3667B2] hover:text-white hover:border-[#3667B2] text-sm lg:text-base font-medium px-2 sm:px-3 py-2 lg:py-2 rounded-md active:scale-95 transition transform duration-300"
                >
                  <span className="md:hidden text-base">
                    <FaClock />
                  </span>
                  <span className="hidden md:flex items-center space-x-2 text-sm lg:text-base">
                    <FaClock />
                    <span>History</span>
                  </span>
                </button>

                <div
                  id={'organization-profile'}
                  ref={userProfileRef}
                >
                  {userProfileImage ? (
                    <img
                      src={userProfileImage}
                      alt="User Profile"
                      className="w-11 h-11 lg:w-11 lg:h-11 rounded-full hover:scale-105 cursor-pointer"
                      onClick={toggleDropdown}
                    />
                  ) : (
                    <FaUser
                      className="w-11 h-11 lg:w-10 lg:h-10 rounded-full hover:scale-105 cursor-pointer"
                      onClick={toggleDropdown}
                    />
                  )}
                </div>
              </div>
            )}
              {/* Dropdown */}
              {dropdownOpen && (
                <div
                  className="fixed top-16 right-4 bg-white shadow-xl rounded-md p-2 w-48 h-auto border border-gray-200"
                  style={{ zIndex: 2147483647, position: 'fixed' }}
                  onClick={() => setDropdownOpen(false)}
                >
                  <button
                    onClick={() => navigate('/organization-profile')}
                    className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    Organization Profile
                  </button>
                  <button
                    onClick={() => setIsPricingModalOpen(true)}
                    className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    Subscription Plans
                  </button>
                  <button
                    onClick={() => navigate('/refer')}
                    className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    Refer & Earn
                  </button>
                  <button 
                    onClick={() => navigate('/invoice')}
                    className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    Invoices
                  </button>
                  <button
                    className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}

              {/* Pricing modal removed — redirect to external pricing page instead */}
            </div>
          </div>

          {/* Mobile outline dropdown */}
          <div className="mt-3 px-2">
            <select
              title="outlineDropdown"
              className="w-full p-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              style={{
                fontSize: "16px", // Prevents zoom on iOS
              }}
              value={currentOutlineID}
              onChange={(e) => {
                if (documentID && dynamicOutlines.length > 0) {
                  const selected = dynamicOutlines.find(
                    (o) => o.outlineID === e.target.value
                  );
                  if (selected) {
                    handleOutlineSelect(
                      selected.title,
                      selected.outlineID,
                      selected.type,
                      true // User selection from dropdown
                    );
                    // Force scroll to selected section on mobile
                    setTimeout(() => {
                      const sectionElement = document.getElementById(`mobile-section-${selected.outlineID}`);
                      if (sectionElement) {
                        sectionElement.scrollIntoView({ 
                          behavior: "smooth", 
                          block: "start",
                          inline: "nearest"
                        });
                      }
                    }, 100);
                  }
                } else {
                  const selected = outlines.find(
                    (o) => o.outlineID === e.target.value
                  );
                  if (selected) {
                    handleOutlineSelect(
                      selected.title,
                      selected.outlineID,
                      selected.type,
                      true // User selection from dropdown
                    );
                  }
                }
              }}
            >
              {(isOutlineGenerated || hasOutlineData) &&
              dynamicOutlines.length > 0 ? (
                dynamicOutlines.map((outline, index) => (
                  <option
                    key={outline.outlineID}
                    value={outline.outlineID}
                    className="py-2 px-3 text-sm"
                  >
                    {index + 1}.{" "}
                    {outline.title.length > 35
                      ? `${outline.title.substring(0, 35)}...`
                      : outline.title}
                  </option>
                ))
              ) : (
                <option value="">Loading outlines...</option>
              )}
            </select>
          </div>
        {/* </div> */}


        <div
          className="flex-1 overflow-y-auto bg-gray-50"
          style={{ 
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            touchAction: "pan-y"
          }}
        >
          <div className="p-3">
            {(isOutlineGenerated || hasOutlineData) &&
            dynamicOutlines.length > 0 ? (
              <div className="flex flex-col gap-4">
                {dynamicOutlines.map((outline, idx) => (
                  <div
                    key={outline.outlineID}
                    id={`mobile-section-${outline.outlineID}`}
                    data-outline-id={outline.outlineID}
                    className="scroll-section bg-white rounded-lg shadow-sm border border-gray-200"
                    style={{
                      touchAction: "pan-y",
                      scrollMarginTop: "20px"
                    }}
                  >
                    <DynamicGeneratedDataViewer
                      documentID={documentID || ""}
                      serverUrl={SOCKET_URL}
                      initialSelectedOutlineID={outline.outlineID}
                      handleRefineClick={handleRefineClick}
                      onRefreshOutlines={fetchOutlineData}
                      outlineData={outline}
                      idx={idx}
                      onDeleteOutline={handleDeleteClick}
                      editable={!isShareMode}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="w-full h-full flex-col items-center justify-center">
                {[...Array(8)].map((_, idx) => (
                  <div
                    key={idx}
                    className="relative group animate-pulse flex items-center justify-center mb-4 w-full"
                  >
                    <div className="w-full p-4 rounded-lg flex flex-col bg-white shadow-sm border border-gray-200">
                      <div className="h-6 bg-gray-300 rounded w-2/3 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-full mb-1"></div>
                      <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center p-3 border-t border-gray-200 bg-white min-h-[60px]">
          {!documentID && (
            <div className="flex items-center gap-3">
              <button
                onClick={handlePaginatePrev}
                disabled={currentSlideIndex === 0}
                className="flex items-center p-2 rounded-md border border-gray-300 disabled:opacity-50"
              >
                <FaArrowLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-600 font-medium">
                Slide {currentSlideIndex + 1} of{" "}
                {slidesArray[currentOutlineID]?.length || 0}
              </span>
              <button
                onClick={handlePaginateNext}
                disabled={
                  currentSlideIndex ===
                  slidesArray[currentOutlineID]?.length - 1
                }
                className="flex items-center p-2 rounded-md border border-gray-300 disabled:opacity-50"
              >
                <FaArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        
      </div>
    </>
    );
   
  };

  // Render desktop view
  const renderDesktopView = () => {
    return (
      <div className="flex flex-col w-full h-full">
        <div className="p-4 bg-white border-b border-gray-200">
          <div className="flex justify-between items-center">
            <a
              className="transition-all duration-300 transform hover:scale-110 active:scale-95 active:opacity-80"
              href="/"
              target="_blank"
            >
              <img src={require('../../assets/zynth-text.png')} alt="Logo Text" className="h-8" />
            </a>
            {!isShareMode && (
              <div className="flex gap-2 items-center">
                {/* Export Button - First */}
                <ExportPresentationButton documentID={documentID} pptName={pptName} outlines={dynamicOutlines.length ? dynamicOutlines : outlines} />
                
                {/* Share Button */}
                {documentID && (
                  <DocumentShareButton 
                    documentID={documentID} 
                    orgId={orgId} 
                    documentName={pptName || 'Document'} 
                  />
                )}
                
                {/* New Presentation Button */}
                <div className="relative group">
                  <button
                    onClick={() => navigate('/new-presentation')}
                    className="bg-[#3667B2] border border-[#3667B2] lg:h-[2.5rem] text-white hover:bg-white hover:text-[#3667B2] hover:border-[#3667B2] text-base font-medium px-3 py-2 lg:py-2 rounded-md active:scale-95 transition transform duration-300"
                  >
                    <span className="md:hidden text-base">
                      <FaChartBar />
                    </span>
                    <span className="hidden md:flex items-center space-x-2">
                      <FaChartBar />
                      <span>New Presentation</span>
                    </span>
                  </button>
                  {/* Tooltip */}
                  <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-max max-w-xs text-center z-50">
                    Start a new AI presentation with slides and visuals.
                  </div>
                </div>

                {/* New Document Button */}
                <div className="relative group">
                  <button
                    onClick={() => navigate('/new-document')}
                    className="bg-[#3667B2] border border-[#3667B2] lg:h-[2.5rem] text-white hover:bg-white hover:text-[#3667B2] hover:border-[#3667B2] text-base font-medium px-3 py-2 lg:py-2 rounded-md active:scale-95 transition transform duration-300"
                  >
                    <span className="md:hidden text-base">
                      <FaFileAlt />
                    </span>
                    <span className="hidden md:flex items-center space-x-2">
                      <FaFileAlt />
                      <span>New Document</span>
                    </span>
                  </button>
                  {/* Tooltip */}
                  <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-max max-w-xs text-center z-50">
                    Start a new AI document like a report, essay, or proposal.
                  </div>
                </div>

                {/* History Button */}
                <button
                  onClick={() => navigate('/history')}
                  className="bg-white lg:h-[2.5rem] border-[#3667B2] border text-[#3667B2] hover:bg-[#3667B2] hover:text-white hover:border-[#3667B2] text-base font-medium px-3 py-2 lg:py-2 rounded-md active:scale-95 transition transform duration-300"
                >
                  <span className="md:hidden text-base">
                    <FaClock />
                  </span>
                  <span className="hidden md:flex items-center space-x-2">
                    <FaClock />
                    <span>History</span>
                  </span>
                </button>
              {/* <div className="relative" >
                <button
                  onClick={(e) => {
                    console.log('Desktop New dropdown button clicked');
                    toggleNewDropdown(e);
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-600 rounded hover:bg-green-50 transition-colors duration-200"
                  type="button"
                  data-dropdown="new"
                >
                  <FaPlus />
                  <span>New</span>
                  <FaChevronDown className="text-xs" />
                </button>
                {showNewDropdown && (
                  <div className="absolute right-0 top-full mt-1 bg-white shadow-lg border border-gray-200 rounded-md p-2 z-[9999] w-48">
                    <button
                      onClick={() => {
                        console.log('Desktop: Navigating to new-presentation');
                         navigate('/new-presentation')
                      }}
                      className="w-full text-left text-sm py-2 px-4 hover:bg-gray-100 rounded block cursor-pointer"
                      type="button"
                    >
                      New Presentation
                    </button>
                    <button
                      onClick={() => {
                        console.log('Desktop: Navigating to new-document');
                         navigate('/new-document')
                        //setShowNewDropdown(false);
                      }}
                      className="w-full text-left text-sm py-2 px-4 hover:bg-gray-100 rounded block"
                      type="button"
                    >
                      New Document
                    </button>
                  </div>
                )}
              </div> */}



                <div
                          id={ 'organization-profile' } // Conditionally apply the ID
                          ref={userProfileRef}
                        >
                          {userProfileImage ? (
                            <img
                              src={userProfileImage}
                              alt="User Profile"
                              className="w-11 h-11 lg:w-11 lg:h-11 rounded-full hover:scale-105 cursor-pointer"
                              onClick={toggleDropdown} // Toggle dropdown when clicked
                            />
                          ) : (
                            <FaUser
                              className="w-11 h-11 lg:w-10 lg:h-10 rounded-full hover:scale-105 cursor-pointer"
                              onClick={toggleDropdown} // Toggle dropdown when clicked
                            />
                          )}
                        </div>
                        {/* Dropdown */}
                        {dropdownOpen && (
                          <div
                             // Attach the reference to the dropdown container
                            className="fixed top-16 right-4 bg-white shadow-xl rounded-md p-2 w-48 h-auto border border-gray-200"
                            style={{ zIndex: 2147483647, position: 'fixed' }}
                            onClick={() => setDropdownOpen(false)} // Close on any click inside
                          >
                            <button
                              onClick={() => navigate('/organization-profile')}
                              className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                            >
                              Organization Profile
                            </button>
                            <button
                              onClick={() => setIsPricingModalOpen(true)}
                              className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                            >
                              Subscription Plans
                            </button>
                            <button
                             onClick={() => navigate('/refer')}
                              className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                            >
                              Refer & Earn
                            </button>
                            <button onClick={() => navigate('/invoice')}
                              className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                          >
                              Invoices
                            </button>
              
                            <button
                              className="w-full text-[#5D5F61] text-left text-sm py-1 px-4 hover:bg-gray-100 overflow-hidden text-ellipsis whitespace-nowrap"
                              onClick={handleLogout}
                            >
                              Logout
                            </button>
                          </div>
                        )}


                        {/* Pricing modal removed — redirect to external pricing page instead */}

              </div>
            )}
          </div>
       
          {/* <div className="flex gap-2">
            {pptName || "Sample Presentation"}
          </div> */}

          {/* <div className="flex gap-2">

            <button
              onClick={exportToDocx}
              disabled={isExporting}
              className={`px-4 py-2 rounded text-white ${isExporting ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {isExporting ? 'Exporting...' : 'Export to DOCX'}
            </button>
          </div> */}
        </div>

        <div className="flex flex-1 h-[calc(100vh-100px)]">
          {/* Sidebar */}
          <div
            ref={sidebarRef}
            className="flex-none bg-white border-r border-gray-200 overflow-y-auto"
            style={{
              width: `${sidebarWidth}px`,
              minWidth: "200px",
              maxWidth: "600px",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain"
            }}
          >
            <div className="p-4">
               
              <h2 className="text-lg font-semibold mb-4">
                 {pptName || "Sample Presentation"}
              </h2>
              <ul className="space-y-2">
                {dynamicOutlines.length > 0 ? (
                  dynamicOutlines.map((outline, index) => (
                    <li
                      key={outline.outlineID}
                      data-outline-id={outline.outlineID}
                      className={`p-3 cursor-pointer rounded-md ${
                        outline.outlineID === currentOutlineID
                          ? "bg-blue-100 text-blue-800"
                          : "hover:bg-gray-100"
                      }`}
                      onClick={() =>
                        handleOutlineSelect(
                          outline.title,
                          outline.outlineID,
                          outline.type,
                          true // User click on outline
                        )
                      }
                    >
                      {index + 1}. {outline.title}
                    </li>
                  ))
                ) : (
                  <div className="w-full h-full flex-col items-center justify-center">
                    {[...Array(5)].map((_, idx) => (
                      <li
                        key={idx}
                        className="relative group animate-pulse flex items-center justify-center mb-2"
                      >
                        <div className="w-full max-w-xs p-3 rounded-lg flex justify-between bg-gray-100">
                          <div className="flex-1">
                            <div className="h-4 bg-gray-300 rounded w-3/4 mb-1"></div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </div>
                )}
              </ul>
            </div>
          </div>

          {/* Resize handle */}
          <div
            ref={resizeRef}
            className="w-1 bg-gray-300 cursor-ew-resize hover:bg-blue-500 transition-colors"
          ></div>

          {/* Content area with continuous scroll */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto"
            style={{ 
              scrollBehavior: "smooth",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y"
            }}
          >
            <div className="p-1 md:p-2 flex flex-col gap-1 md:gap-3">
              {dynamicOutlines.map((outline, idx) => (
                <div
                  key={outline.outlineID}
                  id={`section-${outline.outlineID}`}
                  data-outline-id={outline.outlineID}
                  className="p-1 scroll-section"
                  style={{
                    touchAction: "pan-y",
                    scrollMarginTop: "20px"
                  }}
                >
                  <DynamicGeneratedDataViewer
                    documentID={documentID || ""}
                    serverUrl={SOCKET_URL}
                    initialSelectedOutlineID={outline.outlineID}
                    handleRefineClick={handleRefineClick}
                    onRefreshOutlines={fetchOutlineData}
                    outlineData={outline}
                    idx={idx}
                    onDeleteOutline={handleDeleteClick}
                    editable={!isShareMode}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  };

  // Responsive rendering based on screen size
  return (
    <div className="flex flex-col w-full h-full bg-gray-50">
      {/* Test button for the refine panel */}
      {/* <div className="fixed top-4 left-4 z-50">
        <button
          onClick={() => {
            console.log("🧪 Test button clicked");
            handleRefineClick("Sample text for testing the refine panel functionality. This text should appear in the refine panel for editing.");
          }}
          className="bg-green-500 text-white px-4 py-2 rounded shadow-md hover:bg-green-600"
        >
          Test Refine Panel
        </button>
      </div> */}

      {/* Global refine panel is now replaced by inline panels in SectionRenderer */}
      {/* We're keeping this code commented as reference in case we need to revert
      {showRefinePanel && (
        <div className="fixed top-0 right-0 h-full w-1/3 bg-white shadow-lg z-[9999] flex flex-col overflow-auto">
          <div className="py-4 px-2 w-full bg-white border-l-4 border-blue-500">
            <div className="flex justify-between items-center mb-4">
              <div>
                <button
                  onClick={() => {
                    console.log("Switching to rewrite mode");
                    setRefineMode("rewrite");
                  }}
                  className={`px-4 py-2 mr-2 rounded ${refineMode === "rewrite" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
                >
                  Rewrite & Refine
                </button>
                <button
                  onClick={() => {
                    console.log("Switching to tone mode");
                    setRefineMode("tone");
                  }}
                  className={`px-4 py-2 rounded ${refineMode === "tone" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
                >
                  Tone & Audience
                </button>
              </div>
              <button 
                onClick={() => {
                  console.log("Closing refine panel");
                  setShowRefinePanel(false);
                }}
                className="text-gray-600 hover:text-gray-800"
              >
                <FaArrowRight className="inline" /> Close
              </button>
            </div>
            <div id="refine-panel-content" className="w-full">
              <Suspense fallback={<div className="p-4">Loading refine panel...</div>}>
                {refineMode === "rewrite" ? (
                  <RewriteRefinePanel 
                    selectedText={selectedText}
                    onBack={() => setShowRefinePanel(false)}
                  />
                ) : (
                  <ToneAudiencePanel 
                    selectedText={selectedText}
                    onBack={() => setShowRefinePanel(false)}
                  />
                )}
              </Suspense>
            </div>
          </div>
        </div>
      )} */}

      <div className="hidden md:flex md:flex-col md:h-screen">
        {renderDesktopView()}
      </div>
      <div className="md:hidden flex flex-col h-screen">
        {renderMobileView()}
      </div>
    </div>
  );
}
